import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useRenderCounter } from "@/lib/loop-detector";
import { formatArea, formatAmount, formatActivityLabel } from "@/lib/formatters";
import { useLocation, useRoute } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, ArrowRight, ArrowUp, ArrowDown, GripVertical, Save, Upload, Camera, ChevronDown, ChevronUp, Sparkles, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

import { Badge } from "@/components/ui/badge";
import { FileUpload, type UploadedFile } from "@/components/ui/file-upload";
import { QuoteGenerator, type PlausibilityRestoreHint } from "@/components/project/quote-generator";
import LeistungsverzeichnisKalkulator, { type LVPosition, type LVSummen, type AnnotatedMenge, type MengenRefMap, type MengenBreakdownMap, type ProjectCustomPosition } from "@/components/project/leistungsverzeichnis-kalkulator";
import { AreaMeasurement, type MeasurementResult, getMeasurementAmount, getMeasurementUnit } from "@/components/project/area-measurement";
import { PhotoSchadenserfassung } from "@/components/project/photo-schadenserfassung";
import FacadeReportExport from "@/components/project/facade-report-export";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { insertProjectSchema, type InsertProject, type Project, type LibraryPosition } from "@shared/schema";
import { MultiSelectToggle } from "@/components/ui/multi-select-toggle";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileProjectEdit } from "./mobile/mobile-project-edit";
import { LocalStorageManager, type StoredLvState, type StoredQuoteData, type StoredPhoto } from "@/lib/storage";
import { withProjectLvMetadata, type ProjectCalculations, type ProjectSectionTitles } from "@/lib/project-position-calculation";
import type { PhotoLVPosition } from "@/lib/storage";
import { getPhotoDisplayName } from "@/lib/photo-display-name";
import { PhotoApi } from "@/lib/photo-api";
import {
  photoPositionenMetadataPatch,
  restorePhotoPositionen,
  toQuotePositions,
} from "@/lib/photo-position-order";
import {
  enqueueMeasurementSave,
  flushMeasurementSavesSync,
  dropPendingMeasurementsForPhoto,
} from "@/lib/measurement-save-queue";
import { STAMMDATEN } from "@/components/project/leistungsverzeichnis-kalkulator";
import { prepareMissingServicesImport, type MissingServicesImport } from "@/lib/quote-missing-services";

interface ProjectEditPageProps {
  projectId: string;
}

// ──────────────────────────────────────────────────────────────────────────
// AUTO-SAVE wieder aktiv (Task #3).
//
// Historie: In Commit af73d95 ("Notbremse") wurde Auto-Save komplett
// deaktiviert, nachdem in Produktion eine Render-/Save-Loop mit
// "Maximum Update Depth"-Fehlern und Toast-Spam aufgetreten war
// (React #185, Task #161).
//
// Die Ursache wurde bereits in Commit 3cc45a3 strukturell behoben:
//   1) Form-Init läuft genau einmal pro Projekt-ID
//      (formInitializedForProjectIdRef) — `[project]`-Effekt überschreibt
//      RHF-State nicht mehr bei jedem Refetch.
//   2) updateProjectMutation.onSuccess invalidiert das Detail-Query
//      NICHT mehr (queryClient.setQueryData statt invalidateQueries),
//      sodass keine neue `project`-Referenz die abhängigen Effekte
//      retriggert.
//   3) QuoteGenerator und LV-Kalkulator emittieren nur noch bei
//      inhaltlich geänderten Payloads (lastEmittedHashRef + skipNext-
//      ChangeRef). Reine Referenz-Wechsel bei identischem Inhalt lösen
//      keinen Save mehr aus.
//
// Damit ist die Notbremse nicht mehr nötig. Der Schalter bleibt als
// Kill-Switch erhalten — bei einem Wieder­auftreten der Loop kann er
// in einer Zeile auf `false` zurückgesetzt werden, dann fallen alle
// debounced PATCHes auf "Queue-only, manueller Speichern-Klick flusht"
// zurück.
// ──────────────────────────────────────────────────────────────────────────
const AUTO_SAVE_ENABLED = true;

/** Notbremse: Legacy-Foto-Migration deaktivieren, solange sie React #185 auslöst. */
const LEGACY_PHOTO_MIGRATION_ENABLED = false;

// Leitet die zu persistierenden EP-Overrides aus den aktuellen LV-Positionen ab.
// Regeln:
//  - Composite-IDs (`baseId::unit`) werden separat über `extras` persistiert → skip.
//  - Custom-Positionen (`custom-…`) werden über `customPositionen` persistiert → skip.
//  - Für jede Position, deren ID in `manualEpIds` steht (vom User editierter EP),
//    wird der aktuelle EP als Override geschrieben — überschreibt ggf. einen
//    älteren Eintrag in `baseOverrides`.
//  - `baseOverrides` (z.B. aus Foto-Übernahme) bleiben für IDs erhalten, die nicht
//    aktiv geändert wurden, damit Foto-EPs beim normalen Save nicht verloren gehen.
function deriveEpOverridesFromPositions(
  positionen: LVPosition[],
  manualEpIds: string[],
  baseOverrides: Record<string, number>,
): Record<string, number> {
  // Composite-IDs (`baseId::unit`) und Custom-IDs aus den Basis-Overrides
  // verwerfen — sie werden separat über `extras` bzw. `customPositionen`
  // persistiert. Sonst gelangen sie über den Plausi-EP-Edit-Pfad (der in der
  // Session Composite-IDs nach `lvInitialEpOverrides` schreibt) ungewollt in
  // die `epOverrides`-Spalte des Server-States.
  const next: Record<string, number> = {};
  for (const [id, value] of Object.entries(baseOverrides)) {
    if (id.includes("::")) continue;
    if (id.startsWith("custom-")) continue;
    next[id] = value;
  }
  const manualSet = new Set(manualEpIds);
  for (const p of positionen) {
    if (p.id.includes("::")) continue;
    if (p.id.startsWith("custom-")) continue;
    if (manualSet.has(p.id)) {
      next[p.id] = p.ep;
    }
  }
  return next;
}

// Hofer Seeboden – Fassade · Putz · Sanierung.
// Synchron mit den Listen in `pages/project-create.tsx`.
const objektbeschaffenheitOptions = [
  "Verputztes Mauerwerk",
  "Massivmauerwerk",
  "WDVS-Fassade",
  "Plattenfassade",
  "Sichtbeton (Detail)",
  "Fassade",
  "Sockelbereich",
  "Giebel",
  "Attika",
  "Brüstung",
  "Sims / Gesims",
  "Balkon",
  "Innenputz",
];

const massnahmenOptions = [
  "Fassadenreinigung",
  "Hochdruckreinigung",
  "Algen- und Pilzentfernung",
  "Lose Putzteile abschlagen",
  "Hohlstellen freilegen",
  "Putzausbesserung",
  "Vollflächiger Neuputz",
  "Sanierputz WTA",
  "Armierungsgewebe einbetten",
  "Risssanierung",
  "Hohlstellenverpressung",
  "WDVS-Reparatur",
  "WDVS-Sockelprofil",
  "Sockelsanierung",
  "Betoninstandsetzung",
  "Reprofilierung",
  "Korrosionsschutz",
  "Fassadenanstrich",
  "Silikatfarbe",
  "Silikonharzfarbe",
  "Reinacrylat-Beschichtung",
  "Imprägnierung / Hydrophobierung",
  "Anstrich Holzbauteile",
  "Plattenfassaden-Sanierung",
  "Fugensanierung",
];

const mengeneinheitOptions = ["m²", "lfm", "Stück", "Pauschale"];

export default function ProjectEditPage() {
  const isMobile = useIsMobile();
  const [, params] = useRoute("/project/edit/:id");
  const projectIdFromUrl = params?.id ?? "";
  if (isMobile) {
    return <MobileProjectEdit projectId={projectIdFromUrl} />;
  }
  return <DesktopProjectEditPage />;
}

function DesktopProjectEditPage() {
  useRenderCounter("ProjectEditPage");
  const [, navigate] = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [maxStepReached, setMaxStepReached] = useState(1);
  const totalSteps = 4;
  const [uploadedPhotos, setUploadedPhotos] = useState<UploadedFile[]>([]);
  const [photoInitialFiles, setPhotoInitialFiles] = useState<UploadedFile[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<Array<{ id: string; file: File; type: string }>>([]);
  const [measurementResults, setMeasurementResults] = useState<Map<string, MeasurementResult>>(new Map());
  
  const [lvPositionen, setLvPositionen] = useState<LVPosition[]>([]);
  const [lvSummen, setLvSummen] = useState<LVSummen>({ netto: 0, mwst: 0, brutto: 0 });
  const [lvMengenRefs, setLvMengenRefs] = useState<MengenRefMap>({});
  const [lvInitialMengen, setLvInitialMengen] = useState<Record<string, number>>({});
  const [lvInitialMengenRefs, setLvInitialMengenRefs] = useState<MengenRefMap>({});
  // Per-Foto-Einheits-Varianten, die beim Mount der LV-Kalk als zusätzliche
  // Zeilen unter der Stamm-Position injiziert werden (siehe applyLvFromPhotos).
  const [lvInitialExtras, setLvInitialExtras] = useState<Array<{ baseId: string; unit: string; ep: number; qty: number }>>([]);
  const [lvInitialEpOverrides, setLvInitialEpOverrides] = useState<Record<string, number>>({});
  const [lvManualEpIds, setLvManualEpIds] = useState<string[]>([]);
  const [lvInitialCustomPositionen, setLvInitialCustomPositionen] = useState<ProjectCustomPosition[]>([]);
  const [lvCustomPositionen, setLvCustomPositionen] = useState<ProjectCustomPosition[]>([]);
  const [lvInitialCalculations, setLvInitialCalculations] = useState<ProjectCalculations>({});
  const [lvSectionTitles, setLvSectionTitles] = useState<ProjectSectionTitles>({});
  const lvCalculationsRef = useRef<ProjectCalculations>({});
  const lvSectionTitlesRef = useRef<ProjectSectionTitles>({});
  const lvLatestStateRef = useRef<StoredLvState | null>(null);
  const [lvTeile, setLvTeile] = useState<NonNullable<StoredLvState["teile"]>>({});
  const lvTeileRef = useRef<NonNullable<StoredLvState["teile"]>>({});
  // Ref-Spiegel der zuletzt bekannten Custom-Positionen — verhindert,
  // dass ein paralleler Persistenz-Pfad (z.B. handleLvPositionenChange) mit
  // einem stale State die gerade frisch angelegten Custom-Positionen wieder
  // aus lvState überschreibt.
  const lvCustomPositionenRef = useRef<ProjectCustomPosition[]>([]);
  useEffect(() => {
    lvCustomPositionenRef.current = lvCustomPositionen;
  }, [lvCustomPositionen]);
  // Set der Position-IDs, deren Menge weiterhin automatisch aus der Aufmaß-
  // Gesamtfläche gespeist wird. `null` = noch nicht initialisiert (z.B. weil
  // noch keine Aufmaße existieren oder das Projekt noch nicht hydriert ist).
  const [lvAutoFilledIds, setLvAutoFilledIds] = useState<Set<string> | null>(null);
  // Guard: prevent LV Kalkulator's on-mount emission (STAMMDATEN defaults) from
  // clobbering saved localStorage data before the restore effect has run.
  const [lvHydrated, setLvHydrated] = useState(false);
  // 3-State-Load-Modell für den Server-LV-State.
  // 'not_loaded'  → wir warten noch auf die Server-Antwort. KEINE Persistenz!
  // 'loaded_null' → Server hat kein lvState → optional aus localStorage migrieren.
  // 'loaded_with_data' → Server-State ist die Wahrheit.
  // Solange 'not_loaded' ist, dürfen wir NIE Daten Richtung Server schreiben,
  // sonst überschreibt eine STAMMDATEN-Default-Emission die echten Server-Daten.
  const [lvLoadState, setLvLoadState] = useState<'not_loaded' | 'loaded_null' | 'loaded_with_data'>('not_loaded');
  // Debounced Save-Pipeline für lvState.
  const lvSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lvSavePendingRef = useRef<StoredLvState | null>(null);
  const lvSaveInFlightRef = useRef<Promise<unknown>>(Promise.resolve());
  const lvDirtyRef = useRef(false);

  // ──────────────────────────────────────────────────────────────────────────
  // Analoge 3-State-Hydration für quoteData (Angebotsdaten der Quote-Komponente).
  // Nur die TATSÄCHLICH editierbaren Felder werden persistiert; abgeleitete Items
  // (LV-Positionen, Foto-Aggregate) werden NIE serialisiert, sie kommen aus den
  // bereits eigenständig persistierten Quellen (lvState / Foto-Daten).
  // ──────────────────────────────────────────────────────────────────────────
  const [quoteLoadState, setQuoteLoadState] = useState<'not_loaded' | 'loaded_null' | 'loaded_with_data'>('not_loaded');
  const [initialQuoteData, setInitialQuoteData] = useState<StoredQuoteData | null>(null);
  const quoteSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const quoteSavePendingRef = useRef<StoredQuoteData | null>(null);
  // Counter forces LV-Kalkulator-Remount nach explizitem "Aus Fotos übernehmen",
  // damit die initialMengen-basierte Hydration neu greift (statt internen State zu mutieren).
  const [lvApplyVersion, setLvApplyVersion] = useState(0);
  const [scrollToLvPosId, setScrollToLvPosId] = useState<string | null>(null);
  const [scrollToPhotoIndex, setScrollToPhotoIndex] = useState<number | null>(null);
  const [highlightTarget, setHighlightTarget] = useState<{ index: number; key: number } | null>(null);
  const [photoSchadensData, setPhotoSchadensData] = useState<
    Map<string, { description: string; photoPositionen: PhotoLVPosition[] }>
  >(new Map());
  const [photoSchadensSummaryOpen, setPhotoSchadensSummaryOpen] = useState(false);
  // Maps FileUpload transient IDs → stable LocalStorage photo IDs
  const [photoIdMap, setPhotoIdMap] = useState<Map<string, string>>(new Map());
  // Ref that always mirrors photoIdMap so handlePhotosUploaded can read the
  // current map without having photoIdMap as a useCallback dependency.
  // This prevents a new callback instance (→ FileUpload re-render cascade → blank page)
  // from being created every time a photo is saved and the map is updated.
  const photoIdMapRef = useRef<Map<string, string>>(new Map());
  useEffect(() => { photoIdMapRef.current = photoIdMap; }, [photoIdMap]);

  // ──────────────────────────────────────────────────────────────────────────
  // PRO-FOTO SERVER-PERSISTENZ-PIPELINE
  // Gilt für: description, photoPositionen, measurementData (siehe schedulePhotoSave).
  // Architektur:
  //  - 3-State-Hydration: 'not_loaded' | 'loaded_null' | 'loaded_with_data'.
  //    Solange 'not_loaded' werden KEINE Saves Richtung Server ausgelöst.
  //  - 500 ms Debounce pro Foto.
  //  - 4 s Autosave-Tick: schreibt nur Fotos mit dirty-Flag.
  //  - Inflight-Schutz: während ein Save läuft, weitere Änderungen ins pending
  //    mergen (last-write-wins pro Feld) und nach Abschluss erneut senden.
  //  - Save-Failure: pending bleibt erhalten, dirty bleibt → Autosave retried.
  //  - Migrationsgate: pro Foto und pro Feld nur EINMAL pro Session, nur wenn
  //    Server-Wert eindeutig null/undefined und lokaler Wert strukturell valide.
  //  - Flush bei blur/visibilitychange(hidden)/beforeunload/Unmount.
  // ──────────────────────────────────────────────────────────────────────────
  type PhotoPendingPatch = {
    displayName?: string | null;
    description?: string | null;
    photoPositionen?: PhotoLVPosition[];
    measurementData?: {
      schemaVersion: 1;
      updatedAt: string;
      /** Neues Datenmodell. */
      amount: number;
      amountFormula?: string;
      unit: string;
      mengen: MeasurementResult["mengen"];
      scale: number;
    };
  };
  type PhotoSaveState = {
    pending: PhotoPendingPatch;
    inflight: boolean;
    dirty: boolean;
    debounceTimer: ReturnType<typeof setTimeout> | null;
  };
  const photoSaveStatesRef = useRef<Map<string, PhotoSaveState>>(new Map());
  const [photoLoadState, setPhotoLoadState] = useState<'not_loaded' | 'loaded_null' | 'loaded_with_data'>('not_loaded');
  // Closure-stabiler Spiegel, damit schedulePhotoSave nicht jedes Mal neu erzeugt
  // werden muss und Migration-Aufrufe direkt nach State-Update zuverlässig durchgehen.
  const photoLoadStateRef = useRef<'not_loaded' | 'loaded_null' | 'loaded_with_data'>('not_loaded');
  useEffect(() => { photoLoadStateRef.current = photoLoadState; }, [photoLoadState]);
  // Schlüssel: `${stableId}:description` | `:photoPositionen` | `:measurementData`.
  const photoMigrationDoneRef = useRef<Set<string>>(new Set());

  const getOrInitPhotoSaveState = useCallback((stableId: string): PhotoSaveState => {
    let s = photoSaveStatesRef.current.get(stableId);
    if (!s) {
      s = { pending: {}, inflight: false, dirty: false, debounceTimer: null };
      photoSaveStatesRef.current.set(stableId, s);
    }
    return s;
  }, []);

  const dispatchPhotoSave = useCallback(async (stableId: string): Promise<void> => {
    const s = photoSaveStatesRef.current.get(stableId);
    if (!s) return;
    if (s.inflight) return; // Inflight-Schutz: nichts schicken, finally retried.
    if (Object.keys(s.pending).length === 0) { s.dirty = false; return; }
    const numericId = Number(stableId);
    if (!Number.isInteger(numericId) || numericId <= 0) {
      // Foto noch nicht serverseitig vorhanden — der bestehende enqueuePhotoOp-
      // Pfad triggert nach Upload-Completion einen Direkt-Save mit dem dann
      // aktuellen Patch. pending bleibt erhalten.
      return;
    }
    const payload = s.pending;
    s.pending = {};
    s.dirty = false;
    s.inflight = true;
    try {
      await PhotoApi.updateMetadata(numericId, payload);
    } catch (err) {
      // Save-Failure: Payload zurück in pending mergen (neuere Felder gewinnen),
      // dirty bleibt true. Kein Fake-Erfolg, kein Lösch-Reset.
      console.error('[photo-save] PATCH /api/photos/:id fehlgeschlagen — pending bleibt erhalten:', err);
      s.pending = { ...payload, ...s.pending };
      s.dirty = true;
    } finally {
      s.inflight = false;
      if (s.dirty || Object.keys(s.pending).length > 0) {
        // Während des Saves kamen neue Änderungen ODER Save schlug fehl → erneut.
        void dispatchPhotoSave(stableId);
      }
    }
  }, []);

  const schedulePhotoSave = useCallback((stableId: string, partial: PhotoPendingPatch) => {
    if (photoLoadStateRef.current === 'not_loaded') return; // Hydration-Race
    const s = getOrInitPhotoSaveState(stableId);
    // Last-write-wins pro Feld; pending kumuliert mehrere Felder zu einem PATCH.
    s.pending = { ...s.pending, ...partial };
    s.dirty = true;
    // Kill-Switch: bei AUTO_SAVE_ENABLED=false bleibt der Pending-Patch im Ref
    // liegen und wird erst beim manuellen "Speichern"-Klick via
    // flushAllPhotoSaves gesendet.
    if (!AUTO_SAVE_ENABLED) return;
    if (s.debounceTimer) clearTimeout(s.debounceTimer);
    s.debounceTimer = setTimeout(() => {
      s.debounceTimer = null;
      void dispatchPhotoSave(stableId);
    }, 500);
  }, [getOrInitPhotoSaveState, dispatchPhotoSave]);

  const flushAllPhotoSaves = useCallback(() => {
    photoSaveStatesRef.current.forEach((s, stableId) => {
      if (s.debounceTimer) { clearTimeout(s.debounceTimer); s.debounceTimer = null; }
      if (s.dirty || Object.keys(s.pending).length > 0) {
        void dispatchPhotoSave(stableId);
      }
    });
  }, [dispatchPhotoSave]);

  // 4-Sekunden-Autosave-Tick — feuert NICHT, wenn nichts dirty ist.
  // Nur aktiv, wenn AUTO_SAVE_ENABLED. Sonst wartet der Pending-Patch auf
  // den manuellen Speichern-Klick (siehe saveProgress).
  useEffect(() => {
    if (!AUTO_SAVE_ENABLED) return;
    const t = setInterval(() => {
      let anyDirty = false;
      photoSaveStatesRef.current.forEach((s) => {
        if (s.dirty || Object.keys(s.pending).length > 0) anyDirty = true;
      });
      if (anyDirty) flushAllPhotoSaves();
    }, 4000);
    return () => clearInterval(t);
  }, [flushAllPhotoSaves]);

  // Flush bei Tab-Wechsel/Schließen/Unmount für Foto-Saves.
  useEffect(() => {
    if (!AUTO_SAVE_ENABLED) return;
    const onBlurFlush = () => flushAllPhotoSaves();
    const onVisibility = () => { if (document.visibilityState === 'hidden') flushAllPhotoSaves(); };
    window.addEventListener('blur', onBlurFlush);
    window.addEventListener('beforeunload', onBlurFlush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('blur', onBlurFlush);
      window.removeEventListener('beforeunload', onBlurFlush);
      document.removeEventListener('visibilitychange', onVisibility);
      flushAllPhotoSaves();
    };
  }, [flushAllPhotoSaves]);

  // Buffer for measurement / annotation persistence operations that ran before
  // the photo finished uploading. Drained as soon as the server ID is known.
  const pendingPhotoOpsRef = useRef<Map<string, Array<(serverId: number) => void>>>(new Map());
  const enqueuePhotoOp = (transientId: string, op: (serverId: number) => void) => {
    const list = pendingPhotoOpsRef.current.get(transientId) ?? [];
    list.push(op);
    pendingPhotoOpsRef.current.set(transientId, list);
  };
  const drainPhotoOps = (transientId: string, serverId: number) => {
    const list = pendingPhotoOpsRef.current.get(transientId);
    if (!list || list.length === 0) return;
    pendingPhotoOpsRef.current.delete(transientId);
    for (const op of list) {
      try { op(serverId); } catch (e) { console.error("Pending photo op failed:", e); }
    }
  };

  // Per-Foto-Daten sind die Wahrheitsquelle. Der alte "Aufmaß-Picker" im LV-Kalkulator
  // (availableMengen) wird daher nicht mehr befüllt — bleibt nur als leerer Default für
  // Backwards-Kompatibilität der Komponenten-Props.
  const availableMengen = useMemo<AnnotatedMenge[]>(() => [], []);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Extract project ID from URL
  const projectId = window.location.pathname.split('/').pop();

  const { data: project, isLoading } = useQuery<Project>({
    queryKey: ["/api/projects", projectId],
    enabled: !!projectId,
  });

  // Keep a ref to the latest project name so handlePhotosUploaded can use it
  // without adding `project` to its dependency array (which would change on every fetch)
  const projectNameRef = useRef<string>('Projekt');
  useEffect(() => {
    if (project?.name) projectNameRef.current = project.name;
  }, [project?.name]);

  // On mount: load saved photos, damage data, measurements from LocalStorage cache.
  // ACHTUNG: lvState wird hier NICHT mehr aus localStorage hydriert — das macht
  // ausschließlich der server-first Effekt unten, sobald die Server-Antwort vorliegt.
  useEffect(() => {
    if (!projectId) {
      // Kein Projekt → sofort hydrated, lvLoadState bleibt 'not_loaded' irrelevant.
      setLvHydrated(true);
      setLvLoadState('loaded_null');
      return;
    }
    const storedProject = LocalStorageManager.getProject(projectId);
    if (!storedProject) {
      // Kein lokaler Cache — nur Photo-Restore vom Server (asynchron unten).
      // lvHydrated/lvLoadState werden vom server-first Effekt gesetzt.
    } else {
      // Restore damage data (synchronous, rein als Pre-Population aus lokalem Cache;
      // wird vom Server-Restore unten ggf. überschrieben)
      const initial = new Map<string, { description: string; photoPositionen: PhotoLVPosition[] }>();
      storedProject.photos.forEach((p) => {
        if (p.description || (p.photoPositionen && p.photoPositionen.length > 0)) {
          initial.set(p.id, {
            description: p.description ?? '',
            photoPositionen: p.photoPositionen ?? [],
          });
        }
      });
      if (initial.size > 0) setPhotoSchadensData(initial);
    }

    // Restore photos: migrate any legacy localStorage dataUrl photos to the server,
    // then load authoritative photo metadata from the server.
    //
    // WICHTIG: Die Notbremse `LEGACY_PHOTO_MIGRATION_ENABLED` darf NUR den
    // Migrations-Block (Schritt 1) deaktivieren. Schritt 2+3 (Server-Liste laden,
    // UI-State befüllen) MÜSSEN immer laufen, sonst bleiben die Fotos im UI leer,
    // obwohl sie auf dem Server vorhanden sind.
    (async () => {
      try {
        // 1) One-shot migration: alle Mutationen auf einer In-Memory-Working-Copy,
        //    am Ende GENAU EIN saveProject. Idempotent via legacyPhotoMigrationCompleted.
        if (LEGACY_PHOTO_MIGRATION_ENABLED) {
          const working = LocalStorageManager.getProject(projectId);
          if (working && !working.legacyPhotoMigrationCompleted) {
            const legacyPhotos = working.photos.filter((p) => !!p.dataUrl);
            if (legacyPhotos.length > 0) {
              let allOk = true;
              for (const lp of legacyPhotos) {
                if (!lp.dataUrl) continue;
                try {
                  const created = await PhotoApi.uploadDataUrl(
                    projectId, lp.dataUrl, lp.fileName, lp.fileSize,
                  );
                  const newId = String(created.id);
                  const idx = working.photos.findIndex((p) => p.id === lp.id);
                  if (idx >= 0) {
                    const updated: StoredPhoto = {
                      ...working.photos[idx],
                      id: newId,
                      fileName: created.fileName,
                      fileSize: created.fileSize,
                      mimeType: created.mimeType,
                    };
                    delete updated.dataUrl;
                    working.photos[idx] = updated;
                  }
                  if (working.measurementData) {
                    working.measurementData = working.measurementData.map((m) =>
                      m.photoId === lp.id ? { ...m, photoId: newId } : m,
                    );
                  }
                } catch (mErr) {
                  allOk = false;
                  console.error('Migration eines Legacy-Fotos fehlgeschlagen:', mErr);
                }
              }
              if (allOk) working.legacyPhotoMigrationCompleted = true;
              // GENAU EIN saveProject pro Migrationslauf:
              LocalStorageManager.saveProject(working);
            } else {
              // Keine Legacy-Fotos vorhanden → Flag setzen, EIN saveProject
              working.legacyPhotoMigrationCompleted = true;
              LocalStorageManager.saveProject(working);
            }
          }
        } else {
          console.warn('[migration] Legacy-Foto-Migration durch Notbremse deaktiviert (Server-Restore läuft trotzdem)');
        }

        // 2) Refresh the local snapshot after migration.
        const refreshed = LocalStorageManager.getProject(projectId);
        const localPhotos = refreshed?.photos ?? [];

        // 3) Load authoritative server list (canonical source of truth).
        const serverPhotos = (await PhotoApi.listPhotos(projectId))
          .map((photo, originalIndex) => ({ photo, originalIndex }))
          .sort((a, b) => {
            const aOrder = a.photo.sortOrder;
            const bOrder = b.photo.sortOrder;
            if (aOrder == null && bOrder == null) return a.originalIndex - b.originalIndex;
            if (aOrder == null) return 1;
            if (bOrder == null) return -1;
            return aOrder - bOrder || a.originalIndex - b.originalIndex;
          })
          .map(({ photo }) => photo);

        const restoredFiles: UploadedFile[] = serverPhotos.map((sp) => {
          const stableId = String(sp.id);
          const localMeta = localPhotos.find((p) => p.id === stableId);
          const fileName = localMeta?.fileName ?? sp.fileName;
          const mimeType = localMeta?.mimeType ?? sp.mimeType ?? 'image/jpeg';
          const stubFile = new File([new Blob()], fileName, { type: mimeType });
          // When an annotated version exists, use it as the displayed source so
          // the editing flow shows the latest measurement overlay.
          const previewUrl = sp.hasAnnotation
            ? PhotoApi.getAnnotatedUrl(sp.id)
            : PhotoApi.getPhotoUrl(sp.id);
          return {
            id: stableId,
            file: stubFile,
            displayName: sp.displayName ?? "",
            preview: previewUrl,
            type: 'image' as const,
            serverPhotoId: sp.id,
            displaySize: sp.fileSize,
          };
        });

        // ────────────────────────────────────────────────────────────────────
        // Server-First Hydration für description + photoPositionen.
        // Migration aus localStorage NUR wenn Server-Wert eindeutig null/undefined,
        // lokaler Wert strukturell valide UND Migration in dieser Session noch
        // nicht für dieses Foto + Feld passiert ist.
        // ────────────────────────────────────────────────────────────────────
        const initialSchaden = new Map<string, { description: string; photoPositionen: PhotoLVPosition[] }>();
        // Sammle Migration-Aufrufe und feure sie nach dem Setzen von photoLoadState ab.
        const migrationCalls: Array<{ stableId: string; patch: PhotoPendingPatch }> = [];
        serverPhotos.forEach((sp) => {
          const stableId = String(sp.id);
          const localMeta = localPhotos.find((p) => p.id === stableId);

          // description: Server gewinnt, AUCH bei leerem String, sobald nicht null.
          let description: string;
          const serverHasDescription = sp.description !== null && sp.description !== undefined;
          if (serverHasDescription) {
            description = sp.description as string;
          } else if (
            typeof localMeta?.description === 'string' &&
            localMeta.description.length > 0 &&
            !photoMigrationDoneRef.current.has(`${stableId}:description`)
          ) {
            description = localMeta.description;
            photoMigrationDoneRef.current.add(`${stableId}:description`);
            migrationCalls.push({ stableId, patch: { description } });
          } else {
            description = '';
          }

          // photoPositionen: Server gewinnt, sobald Array — auch wenn leer.
          let photoPositionen: PhotoLVPosition[];
          const serverHasPositionen = Array.isArray(sp.photoPositionen);
          if (serverHasPositionen) {
            photoPositionen = restorePhotoPositionen(sp.photoPositionen);
          } else if (
            Array.isArray(localMeta?.photoPositionen) &&
            (localMeta!.photoPositionen as PhotoLVPosition[]).length > 0 &&
            !photoMigrationDoneRef.current.has(`${stableId}:photoPositionen`)
          ) {
            photoPositionen = localMeta!.photoPositionen as PhotoLVPosition[];
            photoMigrationDoneRef.current.add(`${stableId}:photoPositionen`);
            migrationCalls.push({ stableId, patch: { photoPositionen } });
          } else {
            photoPositionen = [];
          }
          if (description || photoPositionen.length > 0) {
            initialSchaden.set(stableId, { description, photoPositionen });
          }
        });
        if (initialSchaden.size > 0) setPhotoSchadensData(initialSchaden);

        // Pre-populate photoIdMap (stable→stable) so handlePhotosUploaded
        // skips re-uploading restored photos.
        const stableMap = new Map<string, string>();
        restoredFiles.forEach((f) => stableMap.set(f.id, f.id));
        photoIdMapRef.current = stableMap;
        setPhotoIdMap(stableMap);
        setUploadedPhotos(restoredFiles);
        setPhotoInitialFiles(restoredFiles);

        // ────────────────────────────────────────────────────────────────────
        // Restore measurement results: Server gewinnt strikt; Migration aus
        // localStorage nur bei Server-null/undefined + valider lokaler Stand
        // + einmalig pro Session.
        // ────────────────────────────────────────────────────────────────────
        const restoredMeasurements = new Map<string, MeasurementResult>();
        const localMeasurementData = refreshed?.measurementData ?? [];
        serverPhotos.forEach((sp) => {
          const stableId = String(sp.id);
          const md = sp.measurementData as
            | {
                schemaVersion?: number;
                updatedAt?: string;
                amount?: number;
                amountFormula?: string;
                unit?: string;
                netArea?: number;
                totalArea?: number;
                mengen?: unknown;
                scale?: number;
                netAreaOverride?: number;
                openingCount?: number;
                netAreaFormula?: string;
                totalAreaFormula?: string;
              }
            | null
            | undefined;
          const serverAmount = typeof md?.amount === 'number'
            ? md!.amount
            : (typeof md?.netArea === 'number' ? md!.netArea : undefined);
          const serverHasMd = md !== null && md !== undefined && typeof serverAmount === 'number';
          if (serverHasMd) {
            const unit = (md!.unit && md!.unit.length > 0) ? md!.unit : 'm²';
            restoredMeasurements.set(stableId, {
              photoId: stableId,
              amount: serverAmount!,
              amountFormula: typeof md!.amountFormula === 'string'
                ? md!.amountFormula
                : (typeof md!.netAreaFormula === 'string' ? md!.netAreaFormula : undefined),
              unit,
              totalArea: typeof md!.totalArea === 'number' ? md!.totalArea : undefined,
              netArea: typeof md!.netArea === 'number' ? md!.netArea : undefined,
              referencePoints: [],
              openings: [],
              scale: md!.scale ?? 0,
              includeOpenings: false,
              mengen: (md!.mengen as MeasurementResult['mengen']) ?? [],
              netAreaOverride: typeof md!.netAreaOverride === 'number' ? md!.netAreaOverride : undefined,
              openingCount: typeof md!.openingCount === 'number' ? md!.openingCount : undefined,
              netAreaFormula: typeof md!.netAreaFormula === 'string' ? md!.netAreaFormula : undefined,
              totalAreaFormula: typeof md!.totalAreaFormula === 'string' ? md!.totalAreaFormula : undefined,
            });
          } else {
            const localMd = localMeasurementData.find((m) => m.photoId === stableId);
            const localAmount = typeof localMd?.amount === 'number'
              ? localMd!.amount
              : (typeof localMd?.netArea === 'number' ? localMd!.netArea : undefined);
            const localValid = !!localMd && typeof localAmount === 'number' && localAmount > 0;
            if (localValid && !photoMigrationDoneRef.current.has(`${stableId}:measurementData`)) {
              photoMigrationDoneRef.current.add(`${stableId}:measurementData`);
              const unit = (localMd!.unit && localMd!.unit.length > 0) ? localMd!.unit : 'm²';
              const amountFormula = typeof localMd!.amountFormula === 'string'
                ? localMd!.amountFormula
                : (typeof localMd!.netAreaFormula === 'string' ? localMd!.netAreaFormula : undefined);
              restoredMeasurements.set(stableId, {
                photoId: stableId,
                amount: localAmount!,
                amountFormula,
                unit,
                totalArea: typeof localMd!.totalArea === 'number' ? localMd!.totalArea : undefined,
                netArea: typeof localMd!.netArea === 'number' ? localMd!.netArea : undefined,
                referencePoints: [],
                openings: [],
                scale: localMd!.scale,
                includeOpenings: false,
                mengen: localMd!.mengen,
                netAreaOverride: typeof localMd!.netAreaOverride === 'number' ? localMd!.netAreaOverride : undefined,
                openingCount: typeof localMd!.openingCount === 'number' ? localMd!.openingCount : undefined,
                netAreaFormula: typeof localMd!.netAreaFormula === 'string' ? localMd!.netAreaFormula : undefined,
                totalAreaFormula: typeof localMd!.totalAreaFormula === 'string' ? localMd!.totalAreaFormula : undefined,
              });
              migrationCalls.push({
                stableId,
                patch: {
                  measurementData: {
                    schemaVersion: 1,
                    updatedAt: new Date().toISOString(),
                    amount: localAmount!,
                    amountFormula,
                    unit,
                    mengen: localMd!.mengen,
                    scale: localMd!.scale,
                  },
                },
              });
            }
          }
        });
        if (restoredMeasurements.size > 0) setMeasurementResults(restoredMeasurements);

        // 3-State final setzen — ab jetzt darf schedulePhotoSave senden.
        const finalState: 'loaded_with_data' | 'loaded_null' =
          serverPhotos.length > 0 ? 'loaded_with_data' : 'loaded_null';
        photoLoadStateRef.current = finalState; // sofort aktiv für migrationCalls
        setPhotoLoadState(finalState);

        // Einmalige Migrations-Saves abfeuern (Server bleibt Source of Truth).
        for (const m of migrationCalls) {
          schedulePhotoSave(m.stableId, m.patch);
        }
      } catch (err) {
        console.error('Fehler beim Wiederherstellen der Fotos:', err);
        // Selbst bei Hydrationsfehler nicht „not_loaded" stehen lassen — sonst
        // wären spätere User-Eingaben dauerhaft blockiert.
        photoLoadStateRef.current = 'loaded_null';
        setPhotoLoadState('loaded_null');
      }
    })();
  }, [projectId, schedulePhotoSave]);

  // ────────────────────────────────────────────────────────────────────────────
  // LEGACY-MAPPING: Nur historische Stamm-IDs ("p1", "p2", …) aus der Vor-
  // Picker-Zeit auf `lib-<id>` umschreiben, sobald die Bibliothek geladen ist.
  // - Lookup über (Name + Einheit), Vergleich case-insensitive und getrimmt.
  // - Pro Foto + Session genau einmal (Ref-Guard) → idempotent.
  // - Eigene/freie IDs (Custom-Positionen, Import-IDs, …) bleiben strikt
  //   unangetastet, auch bei zufälligem Name+Einheit-Match.
  // - Persistenz erfolgt über die bestehende Foto-Save-Pipeline und das
  //   LocalStorage-Backup, identisch zur normalen Positions-Übernahme.
  // - Positionen ohne Library-Match bleiben unverändert; sie werden in
  //   PhotoSchadenserfassung als "freier/veralteter Eintrag" markiert.
  // ────────────────────────────────────────────────────────────────────────────
  const { data: libraryPositionsForMigration = [] } = useQuery<LibraryPosition[]>({
    queryKey: ["/api/library-positions"],
  });
  const legacyPhotoPosMigratedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (photoLoadState !== 'loaded_with_data') return;
    if (libraryPositionsForMigration.length === 0) return;
    if (photoSchadensData.size === 0) return;

    const norm = (s: string) => s.trim().toLowerCase();
    const libByKey = new Map<string, LibraryPosition>();
    for (const lp of libraryPositionsForMigration) {
      if (lp.aktiv === false) continue;
      libByKey.set(`${norm(lp.name)}|${norm(lp.unit)}`, lp);
    }

    // Strikt nur historische Stamm-IDs aus der Vor-Picker-Zeit ("p1", "p2", …)
    // migrieren — eigene/freie IDs (Custom-Positionen, Import-IDs, etc.) bleiben
    // unangetastet, selbst wenn ihr Name+Einheit zufällig mit einem Bibliotheks-
    // Eintrag übereinstimmt.
    const isLegacyStammId = (id: string) => /^p\d+$/.test(id);

    let mutated = false;
    const next = new Map(photoSchadensData);
    photoSchadensData.forEach((schaden, stableId) => {
      if (legacyPhotoPosMigratedRef.current.has(stableId)) return;
      const hasLegacyId = schaden.photoPositionen.some((p) => isLegacyStammId(p.id));
      legacyPhotoPosMigratedRef.current.add(stableId);
      if (!hasLegacyId) return;
      let positionsChanged = false;
      const newPositionen = schaden.photoPositionen.map((p) => {
        if (!isLegacyStammId(p.id)) return p;
        const lp = libByKey.get(`${norm(p.name)}|${norm(p.unit)}`);
        if (!lp) return p;
        positionsChanged = true;
        return {
          ...p,
          id: `lib-${lp.id}`,
          name: lp.name,
          unit: lp.unit,
        };
      });
      if (!positionsChanged) return;
      next.set(stableId, { ...schaden, photoPositionen: newPositionen });
      if (projectId) {
        LocalStorageManager.updatePhotoData(projectId, stableId, { photoPositionen: newPositionen });
      }
      const numericId = Number(stableId);
      if (Number.isInteger(numericId) && numericId > 0) {
        schedulePhotoSave(stableId, { photoPositionen: newPositionen });
      }
      mutated = true;
    });
    if (mutated) setPhotoSchadensData(next);
  }, [photoLoadState, libraryPositionsForMigration, photoSchadensData, projectId, schedulePhotoSave]);

  // ────────────────────────────────────────────────────────────────────────────
  // SERVER-FIRST HYDRATION FÜR lvState
  // Strikte Regel: Der Server gewinnt IMMER. localStorage ist nur Backup-Cache.
  // - Server hat lvState → daraus hydrieren, lvLoadState='loaded_with_data'.
  // - Server hat kein lvState UND localStorage hat eines für DIESES Projekt
  //   → einmalig vom localStorage in den Server migrieren (PATCH), dann hydrieren.
  // - Server hat kein lvState, localStorage auch nicht → 'loaded_null'.
  // Erst nach diesem Effekt darf scheduleLvSave() Daten Richtung Server schreiben.
  // ────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!projectId) return;
    if (!project) return; // warten auf Server-Antwort des Projekts
    if (lvLoadState !== 'not_loaded') return;

    const serverLvState = (project as Project & { lvState?: StoredLvState | null }).lvState ?? null;

    if (serverLvState && typeof serverLvState === 'object') {
      // Server ist Wahrheit
      lvLatestStateRef.current = serverLvState;
      lvTeileRef.current = serverLvState.teile ?? {};
      setLvTeile(serverLvState.teile ?? {});
      lvCalculationsRef.current = serverLvState.calculations ?? {};
      lvSectionTitlesRef.current = serverLvState.sectionTitles ?? {};
      setLvInitialCalculations(serverLvState.calculations ?? {});
      setLvSectionTitles(serverLvState.sectionTitles ?? {});
      setLvInitialMengen(serverLvState.mengen ?? {});
      setLvInitialMengenRefs(serverLvState.mengenRefs ?? {});
      if (serverLvState.autoFilledPositionIds) {
        setLvAutoFilledIds(new Set(serverLvState.autoFilledPositionIds));
      }
      if (serverLvState.extras && serverLvState.extras.length > 0) {
        setLvInitialExtras(serverLvState.extras);
      }
      if (serverLvState.epOverrides && Object.keys(serverLvState.epOverrides).length > 0) {
        setLvInitialEpOverrides(serverLvState.epOverrides);
      }
      if (serverLvState.customPositionen && serverLvState.customPositionen.length > 0) {
        setLvInitialCustomPositionen(serverLvState.customPositionen);
        setLvCustomPositionen(serverLvState.customPositionen);
      }
      if (serverLvState.manuelleEps && serverLvState.manuelleEps.length > 0) {
        setLvManualEpIds(serverLvState.manuelleEps);
      }
      setLvLoadState('loaded_with_data');
      setLvHydrated(true);
      return;
    }

    // Server hat kein lvState. Optional aus localStorage migrieren.
    const stored = LocalStorageManager.getProject(projectId);
    if (stored && stored.lvState && stored.id === projectId) {
      const local = stored.lvState;
      lvLatestStateRef.current = local;
      lvTeileRef.current = local.teile ?? {};
      setLvTeile(local.teile ?? {});
      lvCalculationsRef.current = local.calculations ?? {};
      lvSectionTitlesRef.current = local.sectionTitles ?? {};
      setLvInitialCalculations(local.calculations ?? {});
      setLvSectionTitles(local.sectionTitles ?? {});
      setLvInitialMengen(local.mengen ?? {});
      setLvInitialMengenRefs(local.mengenRefs ?? {});
      if (local.autoFilledPositionIds) {
        setLvAutoFilledIds(new Set(local.autoFilledPositionIds));
      }
      if (local.extras && local.extras.length > 0) {
        setLvInitialExtras(local.extras);
      }
      if (local.epOverrides && Object.keys(local.epOverrides).length > 0) {
        setLvInitialEpOverrides(local.epOverrides);
      }
      if (local.customPositionen && local.customPositionen.length > 0) {
        setLvInitialCustomPositionen(local.customPositionen);
        setLvCustomPositionen(local.customPositionen);
      }
      if (local.manuelleEps && local.manuelleEps.length > 0) {
        setLvManualEpIds(local.manuelleEps);
      }
      // Einmal-Migration → Server. Bei Fehler bleibt local-Backup intakt.
      // WICHTIG: Vollständigen StoredLvState mitschicken (nicht nur mengen/
      // mengenRefs/autoFilledPositionIds wie in der ursprünglichen
      // pre-Notbremse-Version). Sonst wäre die Migration verlustbehaftet —
      // extras, epOverrides, customPositionen und manuelleEps würden auf dem
      // Server fehlen und beim nächsten Reload (Server-as-truth) gehen die
      // Foto-Übernahme-Varianten, EP-Overrides und eigenen Positionen verloren.
      if (AUTO_SAVE_ENABLED) {
        const payload = {
          mengen: local.mengen ?? {},
          mengenRefs: local.mengenRefs ?? {},
          autoFilledPositionIds: local.autoFilledPositionIds,
          extras: local.extras,
          epOverrides: local.epOverrides,
          customPositionen: local.customPositionen,
          manuelleEps: local.manuelleEps,
          schemaVersion: 1 as const,
          updatedAt: new Date().toISOString(),
        };
        apiRequest('PATCH', `/api/projects/${projectId}/lv-state`, { lvState: payload })
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['/api/projects', projectId] });
            console.log('[lvState] Migration von localStorage → Server erfolgreich.');
          })
          .catch((err) => {
            console.error('[lvState] Migration fehlgeschlagen, localStorage bleibt als Backup:', err);
          });
      }
      setLvLoadState('loaded_with_data');
      setLvHydrated(true);
      return;
    }

    // Wirklich leer
    setLvLoadState('loaded_null');
    setLvHydrated(true);
  }, [projectId, project, lvLoadState, queryClient]);

  // ────────────────────────────────────────────────────────────────────────────
  // Debounced + Flush-fähige Persistenz für lvState.
  // - localStorage: SOFORT als Backup-Schreibung
  // - Server: 500ms debounced PATCH, flush bei blur/visibilitychange/unmount/beforeunload.
  // - Schreibt NUR, wenn lvLoadState !== 'not_loaded' (sonst Race mit Hydration).
  // - Bei Fehler: localStorage-Backup wird NICHT gelöscht und kein Fake-Erfolg gemeldet.
  // ────────────────────────────────────────────────────────────────────────────
  const flushLvSave = useCallback(() => {
    if (!projectId) return;
    if (lvSaveTimerRef.current) {
      clearTimeout(lvSaveTimerRef.current);
      lvSaveTimerRef.current = null;
    }
    const payload = lvSavePendingRef.current;
    if (!payload) return;
    lvSavePendingRef.current = null;
    lvDirtyRef.current = false;
    // sendBeacon-fähig wäre schöner, aber apiRequest reicht — der Browser hält
    // bei beforeunload typischerweise noch lange genug, damit fetch losgeht.
    const save = lvSaveInFlightRef.current.catch(() => undefined).then(() =>
      apiRequest('PATCH', `/api/projects/${projectId}/lv-state`, { lvState: payload }));
    lvSaveInFlightRef.current = save;
    return save
      .catch((err) => {
        // Lokales Backup bleibt intakt, kein Toast-Spam, aber sichtbarer Log.
        console.error('[lvState] Server-Save fehlgeschlagen (lokales Backup bleibt erhalten):', err);
        // Beim nächsten Change wird wieder versucht — markieren nicht „dirty“,
        // sonst läuft Endlos-Schleife.
      });
  }, [projectId]);

  const scheduleLvSave = useCallback((next: StoredLvState) => {
    if (!projectId) return;
    if (lvLoadState === 'not_loaded') return; // Hydration-Race vermeiden
    // All existing save paths replace lvState. Preserve project metadata
    // centrally instead of requiring every quantity/photo callback to copy it.
    next = withProjectLvMetadata(next, lvCalculationsRef.current, lvSectionTitlesRef.current);
    next = { ...next, teile: lvTeileRef.current };
    lvLatestStateRef.current = next;
    // Backup sofort lokal:
    try { LocalStorageManager.saveLvState(projectId, next); } catch (e) {
      console.error('[lvState] localStorage-Backup fehlgeschlagen:', e);
    }
    // Server: payload für späteren manuellen Flush vormerken
    const payload = {
      ...next,
      schemaVersion: 1 as const,
      updatedAt: new Date().toISOString(),
    };
    lvSavePendingRef.current = payload;
    lvDirtyRef.current = true;
    // Kill-Switch: bei AUTO_SAVE_ENABLED=false wartet der Pending-Patch auf
    // den manuellen Speichern-Klick.
    if (!AUTO_SAVE_ENABLED) return;
    if (lvSaveTimerRef.current) clearTimeout(lvSaveTimerRef.current);
    lvSaveTimerRef.current = setTimeout(() => {
      flushLvSave();
    }, 500);
  }, [projectId, lvLoadState, flushLvSave]);

  const handleLvCalculationsChange = useCallback((calculations: ProjectCalculations) => {
    if (!lvHydrated) return;
    lvCalculationsRef.current = calculations;
    setLvInitialCalculations((current) => JSON.stringify(current) === JSON.stringify(calculations) ? current : calculations);
    scheduleLvSave(lvLatestStateRef.current ?? { mengen: {}, mengenRefs: {} });
  }, [lvHydrated, scheduleLvSave]);

  const handleLvTeileChange = useCallback((teile: NonNullable<StoredLvState["teile"]>) => {
    lvTeileRef.current = teile;
    setLvTeile(teile);
    scheduleLvSave(lvLatestStateRef.current ?? { mengen: {}, mengenRefs: {} });
  }, [scheduleLvSave]);

  const handleLvSectionTitlesChange = useCallback((titles: ProjectSectionTitles) => {
    if (!lvHydrated) return;
    lvSectionTitlesRef.current = titles;
    setLvSectionTitles(titles);
    scheduleLvSave(lvLatestStateRef.current ?? { mengen: {}, mengenRefs: {} });
  }, [lvHydrated, scheduleLvSave]);

  // ────────────────────────────────────────────────────────────────────────────
  // SERVER-FIRST HYDRATION für quoteData (Angebot)
  // Identische 3-State-Semantik wie bei lvState.
  // ────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!projectId) return;
    if (!project) return;
    if (quoteLoadState !== 'not_loaded') return;

    const serverQuote = (project as Project & { quoteData?: StoredQuoteData | null }).quoteData ?? null;

    if (serverQuote && typeof serverQuote === 'object') {
      setInitialQuoteData(serverQuote);
      setQuoteLoadState('loaded_with_data');
      return;
    }

    // Server hat kein quoteData. Optional aus localStorage migrieren.
    const stored = LocalStorageManager.getProject(projectId);
    const localQuote = (stored as unknown as { quoteDataV2?: StoredQuoteData } | null)?.quoteDataV2 ?? null;
    if (localQuote && stored?.id === projectId) {
      setInitialQuoteData(localQuote);
      if (AUTO_SAVE_ENABLED) {
        apiRequest('PATCH', `/api/projects/${projectId}/quote`, { quoteData: localQuote })
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['/api/projects', projectId] });
            console.log('[quoteData] Migration von localStorage → Server erfolgreich.');
          })
          .catch((err) => {
            console.error('[quoteData] Migration fehlgeschlagen, localStorage bleibt als Backup:', err);
          });
      }
      setQuoteLoadState('loaded_with_data');
      return;
    }

    setQuoteLoadState('loaded_null');
  }, [projectId, project, quoteLoadState, queryClient]);

  // ────────────────────────────────────────────────────────────────────────────
  // Debounced Save-Pipeline für quoteData. Identische Regeln wie bei lvState:
  // - Schreibt NUR, wenn quoteLoadState !== 'not_loaded'.
  // - Lokales Backup wird sofort geschrieben (in einem eigenen Feld 'quoteDataV2',
  //   damit die alte 'quoteData'-Struktur in StoredProject nicht zerstört wird).
  // - Bei Server-Fehler bleibt das lokale Backup intakt, kein Fake-Erfolg.
  // ────────────────────────────────────────────────────────────────────────────
  const flushQuoteSave = useCallback(() => {
    if (!projectId) return;
    if (quoteSaveTimerRef.current) {
      clearTimeout(quoteSaveTimerRef.current);
      quoteSaveTimerRef.current = null;
    }
    const payload = quoteSavePendingRef.current;
    if (!payload) return;
    quoteSavePendingRef.current = null;
    apiRequest('PATCH', `/api/projects/${projectId}/quote`, { quoteData: payload })
      .catch((err) => {
        console.error('[quoteData] Server-Save fehlgeschlagen (lokales Backup bleibt erhalten):', err);
      });
  }, [projectId]);

  const scheduleQuoteSave = useCallback((next: StoredQuoteData) => {
    if (!projectId) return;
    if (quoteLoadState === 'not_loaded') return; // Hydration-Race vermeiden
    // Zusätzliche Absicherung (Task 97): den zuletzt gemeldeten Stand auch in
    // initialQuoteData spiegeln. Falls die QuoteGenerator-Komponente — entgegen
    // der Hide/Show-Strategie für Schritt 4 — doch einmal aus dem Baum
    // entfernt und wieder eingehängt wird, re-initialisiert sie aus dem
    // aktuellsten Stand statt aus dem ursprünglichen Hydration-Stand.
    // Hinweis: QuoteGenerator nutzt initialQuoteData ausschließlich in
    // useState-Lazy-Initializern, daher wird kein bestehender Komponenten-State
    // durch dieses setState überschrieben.
    setInitialQuoteData(next);
    // Lokales Backup sofort, in eigenem Feld, damit andere Projektdaten unangetastet bleiben.
    try {
      const stored = LocalStorageManager.getProject(projectId);
      if (stored) {
        (stored as unknown as { quoteDataV2?: StoredQuoteData }).quoteDataV2 = next;
        LocalStorageManager.saveProject(stored);
      }
    } catch (e) {
      console.error('[quoteData] localStorage-Backup fehlgeschlagen:', e);
    }
    const payload: StoredQuoteData = {
      ...next,
      schemaVersion: 1,
      updatedAt: new Date().toISOString(),
    };
    quoteSavePendingRef.current = payload;
    // Kill-Switch: bei AUTO_SAVE_ENABLED=false wartet der Pending-Patch auf
    // den manuellen Speichern-Klick.
    if (!AUTO_SAVE_ENABLED) return;
    if (quoteSaveTimerRef.current) clearTimeout(quoteSaveTimerRef.current);
    quoteSaveTimerRef.current = setTimeout(() => {
      flushQuoteSave();
    }, 500);
  }, [projectId, quoteLoadState, flushQuoteSave]);

  // Flush bei Tab-Wechsel/Schließen/Unmount — gemeinsam für lvState und quoteData.
  useEffect(() => {
    if (!AUTO_SAVE_ENABLED) return;
    const onBeforeUnload = () => { flushLvSave(); flushQuoteSave(); };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        flushLvSave();
        flushQuoteSave();
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBeforeUnload);
      // Final-Flush beim Unmount der Edit-Seite (auch bei Projektwechsel).
      flushLvSave();
      flushQuoteSave();
    };
  }, [flushLvSave, flushQuoteSave]);

  // Flush des globalen Measurement-Save-Queues bei beforeunload / Tab-Wechsel /
  // Komponenten-Unmount. Nur lokal (LocalStorage), kein Server-Call — daher
  // unabhängig vom AUTO_SAVE_ENABLED-Flag.
  useEffect(() => {
    const onBeforeUnload = () => flushMeasurementSavesSync();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushMeasurementSavesSync();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onVisibility);
      flushMeasurementSavesSync();
    };
  }, []);

  // ADD-ONLY: handlePhotosUploaded verarbeitet ausschließlich neue Einträge.
  //
  // Fehlende IDs in der eingehenden `files`-Liste werden BEWUSST IGNORIERT —
  // sie dürfen niemals als Lösch-Aktion interpretiert werden (Listen-Diff war
  // die Ursache des Multi-Photo-Crashes mit Datenverlust). Echtes Löschen
  // läuft ausschließlich über `handlePhotoDelete` (Bestätigungsdialog +
  // Server-DELETE + lokales Aufräumen), ausgelöst durch den Lösch-Button im
  // FileUpload via `onDeleteRequest`.
  //
  // photoIdMap ist absichtlich NICHT in der Dependency-Liste — wir lesen sie
  // über photoIdMapRef.current, damit der Callback stabil bleibt.
  const handlePhotosUploaded = useCallback(async (files: UploadedFile[]) => {
    // Update UI immediately
    setUploadedPhotos(files);

    if (!projectId) return;

    try {
      LocalStorageManager.ensureProject(projectId, projectNameRef.current);
    } catch (err) {
      console.error('Fehler beim Initialisieren des Projekts im Speicher:', err);
    }

    // Use the ref so we always see the latest map without recreating this callback
    const currentMap = photoIdMapRef.current;

    // Upload genuinely new photos to the server.
    const newEntries = files.filter((f) => !currentMap.has(f.id) && !f.serverPhotoId);
    const updatedMap = new Map(currentMap);

    for (const f of newEntries) {
      try {
        const created = await PhotoApi.uploadPhoto(projectId, f.file);
        const stableId = String(created.id);
        await PhotoApi.updateMetadata(created.id, {
          sortOrder: files.findIndex((entry) => entry.id === f.id),
        });
        updatedMap.set(f.id, stableId);
        LocalStorageManager.addPhotoMetadata(projectId, {
          id: stableId,
          fileName: created.fileName,
          fileSize: created.fileSize,
          mimeType: created.mimeType,
          uploadDate: new Date().toISOString(),
        });
        // Annotate the in-memory entry with serverPhotoId so consumers can fetch
        // the persisted version (including annotations).
        setUploadedPhotos((prev) =>
          prev.map((p) =>
            p.id === f.id
              ? { ...p, serverPhotoId: created.id, preview: PhotoApi.getPhotoUrl(created.id) }
              : p,
          ),
        );
        // Flush any measurement/annotation persistence that happened while the
        // upload was still in flight.
        drainPhotoOps(f.id, created.id);
        const existingSchaden = photoSchadensData.get(f.id);
        if (existingSchaden) {
          setPhotoSchadensData((prev) => {
            const next = new Map(prev);
            next.delete(f.id);
            next.set(stableId, existingSchaden);
            return next;
          });
        }
      } catch (err) {
        console.error('Fehler beim Hochladen des Fotos:', err);
        toast({
          title: 'Foto-Upload fehlgeschlagen',
          description: err instanceof Error ? err.message : 'Bitte erneut versuchen.',
          variant: 'destructive',
        });
      }
    }

    photoIdMapRef.current = updatedMap;
    setPhotoIdMap(updatedMap);
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const movePhotoBlock = useCallback((fromIndex: number, toIndex: number) => {
    if (
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= uploadedPhotos.length ||
      toIndex >= uploadedPhotos.length ||
      fromIndex === toIndex
    ) return;

    const next = [...uploadedPhotos];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setUploadedPhotos(next);
    setPhotoInitialFiles(next);

    void Promise.all(next.map((photo, index) => {
      const stableId = photoIdMapRef.current.get(photo.id) ?? String(photo.serverPhotoId ?? photo.id);
      if (!/^\d+$/.test(stableId)) return Promise.resolve();
      return PhotoApi.updateMetadata(stableId, { sortOrder: index });
    })).catch((error) => {
      console.error("Fotoreihenfolge konnte nicht gespeichert werden:", error);
      toast({
        title: "Reihenfolge nicht gespeichert",
        description: "Bitte versuche das Verschieben erneut.",
        variant: "destructive",
      });
    });
  }, [uploadedPhotos, toast]);

  // Expliziter Lösch-Pfad — wird nur aufgerufen, wenn der Nutzer im FileUpload
  // den Lösch-Button (X) bewusst klickt. Bestätigungsdialog → Server-DELETE
  // → lokales Aufräumen. Idempotent gegen Server-404 (PhotoApi.deletePhoto
  // behandelt 404 schon intern als Erfolg).
  const handlePhotoDelete = useCallback(async (transientId: string) => {
    const stable = photoIdMapRef.current.get(transientId);
    const photoLabel = uploadedPhotos.find((p) => p.id === transientId)?.file.name ?? 'Foto';
    if (!window.confirm(`„${photoLabel}" wirklich löschen?`)) return;

    if (!projectId) {
      setUploadedPhotos((prev) => prev.filter((p) => p.id !== transientId));
      return;
    }

    if (stable) {
      const numericId = Number(stable);
      if (Number.isFinite(numericId) && numericId > 0) {
        try {
          await PhotoApi.deletePhoto(numericId);
        } catch (err) {
          console.error('Foto-Löschen fehlgeschlagen:', err);
          toast({
            title: 'Foto-Löschen fehlgeschlagen',
            description: err instanceof Error ? err.message : 'Bitte erneut versuchen.',
            variant: 'destructive',
          });
          return;
        }
      }
      LocalStorageManager.removePhotoMetadata(projectId, stable);
      dropPendingMeasurementsForPhoto(projectId, stable);
    }
    dropPendingMeasurementsForPhoto(projectId, transientId);

    setUploadedPhotos((prev) => prev.filter((p) => p.id !== transientId));
    // measurementResults kann je nach Foto-Lebenszyklus per transientId ODER per
    // stableId (Server-Restore) gekeyed sein — beide Varianten beim Löschen
    // entfernen, sonst bleibt ein Geistereintrag, der im Quote-Generator
    // photos/measurementResults asymmetrisch macht und React #185 begünstigt.
    setMeasurementResults((prev) => {
      const hasTransient = prev.has(transientId);
      const hasStable = stable ? prev.has(stable) : false;
      if (!hasTransient && !hasStable) return prev;
      const next = new Map(prev);
      if (hasTransient) next.delete(transientId);
      if (hasStable && stable) next.delete(stable);
      return next;
    });
    if (stable) {
      photoIdMapRef.current.delete(transientId);
      setPhotoIdMap(new Map(photoIdMapRef.current));
    }
  }, [projectId, uploadedPhotos, toast]);

  // Returns the stable stored photo ID for a given FileUpload transient ID
  const getStablePhotoId = (transientId: string): string =>
    photoIdMap.get(transientId) ?? transientId;

  const updatePhotoSchaden = (
    transientPhotoId: string,
    field: "description" | "photoPositionen",
    value: string | PhotoLVPosition[]
  ) => {
    const stableId = getStablePhotoId(transientPhotoId);
    setPhotoSchadensData((prev) => {
      const existing = prev.get(stableId) ?? { description: "", photoPositionen: [] };
      const updated = { ...existing, [field]: value };
      const next = new Map(prev);
      next.set(stableId, updated);
      if (projectId) {
        if (field === "description") {
          LocalStorageManager.updatePhotoData(projectId, stableId, { description: value as string });
        } else {
          LocalStorageManager.updatePhotoData(
            projectId,
            stableId,
            photoPositionenMetadataPatch(value as PhotoLVPosition[]),
          );
        }
      }
      // Server-Persistenz läuft über die Pro-Foto-Pipeline (Debounce + Inflight + Retry).
      const numericId = Number(stableId);
      const patch: PhotoPendingPatch = field === "description"
        ? { description: value as string }
        : photoPositionenMetadataPatch(value as PhotoLVPosition[]);
      if (Number.isInteger(numericId) && numericId > 0) {
        schedulePhotoSave(stableId, patch);
      } else {
        // Upload läuft noch — direkter Save nach Erhalt der Server-ID.
        enqueuePhotoOp(transientPhotoId, (serverId) => {
          PhotoApi.updateMetadata(serverId, patch).catch((err) =>
            console.error("Foto-Metadaten (post-upload) nicht gespeichert:", err),
          );
        });
      }
      return next;
    });
  };

  const form = useForm<InsertProject>({
    resolver: zodResolver(insertProjectSchema),
    defaultValues: {
      name: "",
      type: "",
      customer: "",
      address: "",
      description: "",
      status: "draft",
      currentStep: 1,
      objektbeschaffenheit: [],
      massnahmen: [],
      geschosszahl: undefined,
      hoehe: undefined,
      menge: undefined,
      mengeneinheit: "m²",
    },
    mode: "onBlur",
  });

  // Update form when project data loads.
  //
  // ⚠️ Loop-Schutz (React #185 in Produktion):
  // Dieser Effekt darf NICHT bei jedem Refetch von `project` neu feuern. Sonst
  // entsteht die Kette:
  //   updateProjectMutation.onSuccess → invalidate(['/api/projects', id])
  //   → Refetch → neue `project`-Referenz → dieser Effekt → form.reset/setCurrentStep
  //   → ggf. weitere abhängige Effekte → erneuter Save → …
  // Wir initialisieren das Formular daher genau einmal pro Projekt-ID. Während
  // der User editiert, ist der RHF-State die Wahrheit; Server-Refetches dürfen
  // ihn nicht überschreiben (sonst ginge auch die User-Eingabe verloren).
  const formInitializedForProjectIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!project || !projectId) return;
    if (formInitializedForProjectIdRef.current === projectId) return;
    formInitializedForProjectIdRef.current = projectId;
    const normalizedStep = Math.min(Math.max(project.currentStep ?? 1, 1), totalSteps);
    form.reset({
      name: project.name,
      type: project.type,
      customer: project.customer || "",
      address: project.address,
      description: project.description || "",
      status: project.status,
      currentStep: normalizedStep,
      objektbeschaffenheit: project.objektbeschaffenheit ?? [],
      massnahmen: project.massnahmen ?? [],
      geschosszahl: project.geschosszahl ?? undefined,
      hoehe: project.hoehe ?? undefined,
      menge: project.menge ?? undefined,
      mengeneinheit: project.mengeneinheit ?? "m²",
    });
    setCurrentStep(normalizedStep);
    setMaxStepReached((prev) => Math.max(prev, normalizedStep));
  }, [project, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateProjectMutation = useMutation({
    mutationFn: async (data: Partial<InsertProject>) => {
      const response = await apiRequest("PUT", `/api/projects/${projectId}`, data);
      return response.json() as Promise<Project>;
    },
    onSuccess: (updated) => {
      // ⚠️ Loop-Schutz (React #185 in Produktion):
      // Wir invalidieren das Detail-Query bewusst NICHT. Eine Invalidation würde
      // einen Refetch auslösen, der eine neue `project`-Referenz liefert und damit
      // den `[project]`-Effekt erneut feuern lassen würde. Stattdessen aktualisieren
      // wir den Cache mit der Server-Antwort direkt — multi-Tab-Sync passiert
      // weiterhin beim nächsten Laden/Refetch der Detailseite.
      // `exact: true` verhindert, dass das Listen-Query versehentlich auch das
      // Detail-Query (gleicher Prefix) invalidiert.
      if (updated && projectId) {
        queryClient.setQueryData(["/api/projects", projectId], updated);
      }
      queryClient.invalidateQueries({ queryKey: ["/api/projects"], exact: true });
      queryClient.invalidateQueries({ queryKey: ["/api/projects/stats"] });
      toast({
        title: "Projekt aktualisiert",
        description: "Die Änderungen wurden erfolgreich gespeichert.",
      });
    },
    onError: () => {
      toast({
        title: "Fehler beim Speichern",
        description: "Das Projekt konnte nicht aktualisiert werden.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = async (data: InsertProject) => {
    // Bereits abgeschlossene Projekte dürfen durch ein zusätzliches manuelles
    // „Speichern" auf Schritt 4 NICHT zurück auf „active" gesetzt werden,
    // sonst verschwinden sie aus der Seite „Abgeschlossene Angebote".
    const nextStatus =
      project?.status === "completed"
        ? "completed"
        : currentStep === totalSteps
          ? "active"
          : "draft";
    await updateProjectMutation.mutateAsync({
      ...data,
      currentStep,
      status: nextStatus,
    });
  };

  const goToStep = (newStep: number) => {
    if (newStep < 1 || newStep > totalSteps || newStep === currentStep) return;
    setCurrentStep(newStep);
    setMaxStepReached((prev) => Math.max(prev, newStep));
    if (AUTO_SAVE_ENABLED) {
      // Alle Formularfelder (inkl. objektbeschaffenheit, massnahmen, eigene Einträge)
      // mitschicken, damit Step-Navigation keine Änderungen verwirft.
      const formValues = form.getValues();
      updateProjectMutation.mutate({ ...formValues, currentStep: newStep });
    }
  };

  const handleScrollToProjectDescription = useCallback(() => {
    const focusDescription = () => {
      const el = document.getElementById('description') as HTMLTextAreaElement | null;
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Kurze visuelle Hervorhebung + Fokus, damit der Nutzer das
      // Pflichtfeld sofort findet.
      el.focus({ preventScroll: true });
      el.classList.add('ring-2', 'ring-bauwerk-orange');
      window.setTimeout(() => {
        el.classList.remove('ring-2', 'ring-bauwerk-orange');
      }, 2000);
    };
    if (currentStep === 1) {
      focusDescription();
    } else {
      goToStep(1);
      window.setTimeout(focusDescription, 200);
    }
  }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScrollToLvPosition = useCallback((posId: string) => {
    if (currentStep === 4) {
      setScrollToLvPosId(posId);
    } else {
      goToStep(4);
      setTimeout(() => setScrollToLvPosId(posId), 150);
    }
  }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // ──────────────────────────────────────────────────────────────────────────
  // Rücksprung aus der Plausibilitätsprüfung
  // Wird im Plausi-Block angeklickt → wir merken uns Ursprungs-Position +
  // aufgeklappte Karten (in-memory, nur für diese Session) und scrollen zur
  // betreffenden LV-Zeile. Der LV-Schritt zeigt dann oben einen „Zurück zur
  // Plausibilitätsprüfung"-Streifen, der beim Klick einen Restore-Hint an den
  // QuoteGenerator schickt (Block + Karte aufklappen, hinscrollen).
  // ──────────────────────────────────────────────────────────────────────────
  const [plausibilityReturn, setPlausibilityReturn] = useState<{
    posId: string;
    expandedPositionIds: string[];
  } | null>(null);
  const [plausibilityRestoreHint, setPlausibilityRestoreHint] =
    useState<PlausibilityRestoreHint | null>(null);

  const handleJumpFromPlausibility = useCallback(
    (posId: string, expandedPositionIds: string[]) => {
      setPlausibilityReturn({ posId, expandedPositionIds });
      handleScrollToLvPosition(posId);
    },
    [handleScrollToLvPosition],
  );

  const handleReturnToPlausibility = useCallback(() => {
    if (!plausibilityReturn) return;
    setPlausibilityRestoreHint({
      posId: plausibilityReturn.posId,
      expandedPositionIds: plausibilityReturn.expandedPositionIds,
      nonce: Date.now(),
    });
    setPlausibilityReturn(null);
  }, [plausibilityReturn]);

  const handlePlausibilityRestoreConsumed = useCallback(() => {
    setPlausibilityRestoreHint(null);
  }, []);

  // Pro LV-Position-ID Anzahl & Titel der Plausibilitätsbefunde — wird vom
  // QuoteGenerator beim Neuberechnen der Befunde gemeldet und an den
  // LV-Kalkulator durchgereicht. Damit kann jede LV-Zeile mit Befund ein
  // dezentes Warn-Symbol mit Tooltip anzeigen, ohne dass der Kalkulator selbst
  // die Plausibilitätslogik kennen muss.
  const [plausibilityByLvPosId, setPlausibilityByLvPosId] = useState<
    Record<string, { count: number; titles: string[] }>
  >({});

  const handlePlausibilityFindingsByPosIdChange = useCallback(
    (map: Record<string, { count: number; titles: string[] }>) => {
      setPlausibilityByLvPosId(map);
    },
    [],
  );

  // Direkter Sprung aus einer LV-Zeile in den passenden Plausi-Eintrag.
  // Nutzt denselben Restore-Hint-Mechanismus wie der „Zurück"-Streifen, klappt
  // also den Plausi-Block auf, expandiert die Karte zur Position und scrollt
  // dorthin. `plausibilityReturn` wird hier bewusst NICHT gesetzt, weil der
  // Nutzer nicht aus dem Plausi-Block ins LV gekommen ist — sonst würde der
  // Rücksprung-Streifen oben im LV irreführend erscheinen.
  const handleJumpToPlausibilityFromLv = useCallback((posId: string) => {
    setPlausibilityRestoreHint({
      posId,
      expandedPositionIds: [posId],
      nonce: Date.now(),
    });
  }, []);

  // Inline-EP-Edit aus der Plausibilitätsprüfung. Schreibt für die LV-
  // Position einen neuen EP-Override (entspricht dem Effekt einer manuellen
  // Eingabe in der LV-Tabelle): die ID landet in `lvManualEpIds`,
  // `lvInitialEpOverrides` wird aktualisiert und persistiert. Der LV-
  // Kalkulator zieht den neuen Wert über den Re-Hydration-Effect aus
  // `initialEpOverrides` und markiert die Zeile als „MANUELL".
  const handlePlausibilityEpUpdate = useCallback(
    (posId: string, ep: number, unit?: string) => {
      if (!projectId || !lvHydrated) return;
      if (!Number.isFinite(ep) || ep < 0) return;
      const rounded = Math.round(ep * 100) / 100;

      // Plausi-Findings vom Typ „photo-agg" liefern als `lvPositionId` die
      // Basis-ID der Position (z.B. `lib-123`). Wenn die zugehörige LV-Zeile
      // tatsächlich als Composite-Extra (`lib-123::Psch`) existiert (Foto-
      // Einheit weicht von Stamm-/Library-Einheit ab), würde ein Override
      // unter der Basis-ID ins Leere greifen: die Extra-Zeile bleibt mit
      // altem EP, und beim Save filtert `deriveEpOverridesFromPositions` die
      // Composite-IDs ohnehin heraus. Wir resolven daher hier zuerst auf die
      // konkret in `lvPositionen` vorhandene Ziel-Zeile.
      let targetId = posId;
      if (unit) {
        const compositeId = `${posId}::${unit}`;
        if (lvPositionen.some((p) => p.id === compositeId)) {
          targetId = compositeId;
        } else {
          const exact = lvPositionen.find(
            (p) => p.id === posId && p.unit === unit,
          );
          if (!exact) {
            // Fallback: irgendeine Composite-Zeile mit derselben Basis-ID +
            // Einheit, falls die Stammzeile fehlt (z.B. STAMMDATEN-Position
            // ohne Mengen-Eintrag).
            const compositeFallback = lvPositionen.find(
              (p) => p.id.startsWith(`${posId}::`) && p.unit === unit,
            );
            if (compositeFallback) targetId = compositeFallback.id;
          }
        }
      }
      const nextOverrides = { ...lvInitialEpOverrides, [targetId]: rounded };
      setLvInitialEpOverrides(nextOverrides);
      const nextManualIds = lvManualEpIds.includes(targetId)
        ? lvManualEpIds
        : [...lvManualEpIds, targetId];
      if (nextManualIds !== lvManualEpIds) {
        setLvManualEpIds(nextManualIds);
      }

      // WICHTIG: Den neuen EP beim Aufbau der zu persistierenden `extras`
      // direkt einsetzen. Sonst würden wir die Extras mit dem ALTEN EP aus
      // `lvPositionen` flushen (der LV-Kalkulator hat seinen State erst nach
      // dem nächsten Render aktualisiert) — und da Auto-Save deaktiviert ist,
      // würde der nachfolgende `handleLvPositionenChange`-Aufruf nur den
      // Pending-Payload überschreiben, ohne ihn jemals zu flushen.
      const mengen: Record<string, number> = {};
      const extras: Array<{ baseId: string; unit: string; ep: number; qty: number }> = [];
      lvPositionen.forEach((p) => {
        const effectiveEp = p.id === targetId ? rounded : p.ep;
        if (p.id.includes("::")) {
          const [baseId, u] = p.id.split("::");
          if (p.qty > 0 && u) extras.push({ baseId, unit: u, ep: effectiveEp, qty: p.qty });
          return;
        }
        if (p.id.startsWith("custom-")) return;
        if (p.qty > 0) mengen[p.id] = p.qty;
      });
      const activeIds = new Set(lvPositionen.map((p) => p.id));
      STAMMDATEN.forEach((s) =>
        s.positionen.forEach((p) => {
          if (p.aktiv && !activeIds.has(p.id)) mengen[p.id] = 0;
        }),
      );

      // Composite-IDs (`baseId::unit`) werden separat über `extras`
      // persistiert — sie dürfen nicht in `epOverrides` landen, sonst
      // produziert die Re-Hydration nach Reload Doppel-Updates und der
      // `deriveEpOverridesFromPositions`-Filter wäre inkonsistent.
      const overridesForPersistence: Record<string, number> = {};
      for (const [id, value] of Object.entries(nextOverrides)) {
        if (id.includes("::")) continue;
        if (id.startsWith("custom-")) continue;
        overridesForPersistence[id] = value;
      }
      // Falls der User einen Composite-Extra editiert hat, gibt es keinen
      // Eintrag in `manuelleEps`, der für die Persistenz nötig wäre —
      // Composites werden vollständig über `extras` rekonstruiert. Wir
      // filtern Composite-IDs aus dem Persistenz-Payload, damit die Server-
      // Liste nicht mit Composite-IDs aufgebläht wird, die `deriveEpOverrides…`
      // ohnehin ignoriert.
      const manualIdsForPersistence = nextManualIds.filter(
        (id) => !id.includes("::") && !id.startsWith("custom-"),
      );

      scheduleLvSave({
        mengen,
        mengenRefs: lvMengenRefs,
        autoFilledPositionIds: lvAutoFilledIds ? Array.from(lvAutoFilledIds) : undefined,
        extras: extras.length > 0 ? extras : undefined,
        epOverrides:
          Object.keys(overridesForPersistence).length > 0
            ? overridesForPersistence
            : undefined,
        customPositionen:
          lvCustomPositionenRef.current.length > 0
            ? lvCustomPositionenRef.current
            : undefined,
        manuelleEps: manualIdsForPersistence.length > 0 ? manualIdsForPersistence : undefined,
      });
      // Sofort an den Server flushen — der Toast „EP gespeichert" verspricht
      // dem Nutzer Persistenz; bei Reload würde sonst der Server-Stand
      // (ohne den frischen Override) gewinnen.
      flushLvSave();
    },
    [
      projectId,
      lvHydrated,
      lvInitialEpOverrides,
      lvManualEpIds,
      lvPositionen,
      lvMengenRefs,
      lvAutoFilledIds,
      scheduleLvSave,
      flushLvSave,
    ],
  );

  const handleGoToMeasurementPhoto = useCallback((imageIndex: number) => {
    if (currentStep === 3) {
      setScrollToPhotoIndex(imageIndex);
    } else {
      goToStep(3);
      setTimeout(() => setScrollToPhotoIndex(imageIndex), 150);
    }
  }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLvPositionenChange = useCallback((positionen: LVPosition[], summen: LVSummen, refs: MengenRefMap) => {
    setLvPositionen(positionen);
    setLvSummen(summen);
    setLvMengenRefs(refs);
    // Guard: do not persist while hydrating — the LV Kalkulator emits onPositionenChange on
    // its own mount (with STAMMDATEN defaults, incl. default-active p32/p34), which would
    // overwrite saved localStorage data before the restore effect has completed.
    if (!lvHydrated || !projectId) return;
    const mengen: Record<string, number> = {};
    // Composite-ID-Zeilen (`${baseId}::${unit}`) sind injizierte Per-Foto-Extras
    // → separat als `extras` persistieren, NICHT in `mengen`.
    const extras: Array<{ baseId: string; unit: string; ep: number; qty: number }> = [];
    positionen.forEach(p => {
      if (p.id.includes("::")) {
        const [baseId, unit] = p.id.split("::");
        if (p.qty > 0 && unit) {
          extras.push({ baseId, unit, ep: p.ep, qty: p.qty });
        }
        return;
      }
      // Custom-Positionen werden separat über `customPositionen` (vollständige
      // Stammdaten) persistiert — hier in `mengen` überspringen, sonst doppelt.
      if (p.id.startsWith("custom-")) return;
      if (p.qty > 0) mengen[p.id] = p.qty;
    });
    // Persist explicit deactivation for positions that are active by default in STAMMDATEN
    // (so they are restored as deactivated on reload, not reactivated from STAMMDATEN)
    const activeIds = new Set(positionen.map(p => p.id));
    STAMMDATEN.forEach(s => s.positionen.forEach(p => {
      if (p.aktiv && !activeIds.has(p.id)) {
        mengen[p.id] = 0; // explicit zero = "was default-active, now deactivated"
      }
    }));
    // EP-Overrides aus den aktuellen Positionen ableiten — sonst gehen vom User
    // editierte EP-Werte (z.B. p32, p34, lib-…) beim Save verloren und tauchen
    // nach Reload mit STAMMDATEN-/Library-Defaults wieder auf (Bug: PDF zeigt
    // alten EP). Der gemerkte Snapshot wird auch lokal aktualisiert, damit
    // nachfolgende Saves (Custom/Manual) konsistent bleiben.
    const nextEpOverrides = deriveEpOverridesFromPositions(
      positionen,
      lvManualEpIds,
      lvInitialEpOverrides,
    );
    if (JSON.stringify(nextEpOverrides) !== JSON.stringify(lvInitialEpOverrides)) {
      setLvInitialEpOverrides(nextEpOverrides);
    }
    // Composite-/Custom-IDs aus `manuelleEps`-Persistenz filtern — sie
    // werden separat über `extras`/`customPositionen` rekonstruiert und
    // tragen in `manuelleEps` keine semantische Information.
    const manualIdsForPersistence = lvManualEpIds.filter(
      (id) => !id.includes("::") && !id.startsWith("custom-"),
    );
    scheduleLvSave({
      mengen,
      mengenRefs: refs,
      autoFilledPositionIds: lvAutoFilledIds ? Array.from(lvAutoFilledIds) : undefined,
      extras: extras.length > 0 ? extras : undefined,
      epOverrides: Object.keys(nextEpOverrides).length > 0 ? nextEpOverrides : undefined,
      customPositionen:
        lvCustomPositionenRef.current.length > 0 ? lvCustomPositionenRef.current : undefined,
      manuelleEps: manualIdsForPersistence.length > 0 ? manualIdsForPersistence : undefined,
    });
  }, [projectId, lvHydrated, lvAutoFilledIds, scheduleLvSave, lvInitialEpOverrides, lvManualEpIds]);

  const handleLvCustomPositionenChange = useCallback((cps: ProjectCustomPosition[]) => {
    // Ref SYNCHRON aktualisieren, bevor irgendein anderer Save-Pfad
    // (z.B. handleLvPositionenChange im selben Render-Tick) den Wert liest.
    // Der Effect, der den Ref normalerweise spiegelt, läuft erst nach dem
    // Commit — das wäre zu spät und ist die dokumentierte Race-Quelle.
    lvCustomPositionenRef.current = cps;
    setLvCustomPositionen(cps);
    if (!lvHydrated || !projectId) return;
    // Re-Persist mit den aktuellen anderen lvState-Feldern. Wir lesen den
    // letzten Mengen-Snapshot aus den aktuell aktiven Positionen, damit wir
    // nicht in die Falle laufen, mengen mit Defaults zu überschreiben.
    const mengen: Record<string, number> = {};
    const extras: Array<{ baseId: string; unit: string; ep: number; qty: number }> = [];
    lvPositionen.forEach((p) => {
      if (p.id.includes("::")) {
        const [baseId, unit] = p.id.split("::");
        if (p.qty > 0 && unit) extras.push({ baseId, unit, ep: p.ep, qty: p.qty });
        return;
      }
      if (p.id.startsWith("custom-")) return;
      if (p.qty > 0) mengen[p.id] = p.qty;
    });
    const activeIds = new Set(lvPositionen.map((p) => p.id));
    STAMMDATEN.forEach((s) =>
      s.positionen.forEach((p) => {
        if (p.aktiv && !activeIds.has(p.id)) mengen[p.id] = 0;
      })
    );
    // EP-Overrides aus aktuellen Positionen ableiten (siehe handleLvPositionenChange).
    const nextEpOverrides = deriveEpOverridesFromPositions(
      lvPositionen,
      lvManualEpIds,
      lvInitialEpOverrides,
    );
    const manualIdsForPersistence = lvManualEpIds.filter(
      (id) => !id.includes("::") && !id.startsWith("custom-"),
    );
    scheduleLvSave({
      mengen,
      mengenRefs: lvMengenRefs,
      autoFilledPositionIds: lvAutoFilledIds ? Array.from(lvAutoFilledIds) : undefined,
      extras: extras.length > 0 ? extras : undefined,
      epOverrides: Object.keys(nextEpOverrides).length > 0 ? nextEpOverrides : undefined,
      customPositionen: cps.length > 0 ? cps : undefined,
      manuelleEps: manualIdsForPersistence.length > 0 ? manualIdsForPersistence : undefined,
    });
  }, [projectId, lvHydrated, lvPositionen, lvMengenRefs, lvAutoFilledIds, lvInitialEpOverrides, scheduleLvSave, lvManualEpIds]);

  const handleImportMissingServices = useCallback((request: MissingServicesImport) => {
    if (!lvHydrated || !projectId) throw new Error("Das LV ist noch nicht vollständig geladen.");
    const imported = prepareMissingServicesImport(
      request,
      [...lvPositionen.map((p) => p.name), ...lvCustomPositionenRef.current.map((p) => p.name)],
      ["s7"],
    );
    if (imported.positions.length === 0) return;
    const next = [...lvCustomPositionenRef.current, ...imported.positions];
    // Set part metadata before saving the rows: every LV save reads this ref.
    handleLvTeileChange({ ...lvTeileRef.current, ...imported.teile });
    handleLvCustomPositionenChange(next);
    // Existing calculator hydration merges new IDs without resetting edited rows.
    setLvInitialCustomPositionen(next);
    toast({
      title: `${imported.positions.length} Position(en) ins LV übernommen`,
      description: "Unter Pauschalleistungen können Sie den Preis ergänzen und bei Bedarf Menge und Einheit anpassen.",
    });
  }, [lvHydrated, projectId, lvPositionen, handleLvTeileChange, handleLvCustomPositionenChange, toast]);

  const handleLvManualEpIdsChange = useCallback((ids: string[]) => {
    setLvManualEpIds(ids);
    if (!lvHydrated || !projectId) return;
    // EP-Overrides anhand der NEUEN manualEpIds + aktuellen Positionen ableiten,
    // damit ein gerade als "manuell" markierter EP sofort mitpersistiert wird.
    const nextEpOverrides = deriveEpOverridesFromPositions(
      lvPositionen,
      ids,
      lvInitialEpOverrides,
    );
    if (JSON.stringify(nextEpOverrides) !== JSON.stringify(lvInitialEpOverrides)) {
      setLvInitialEpOverrides(nextEpOverrides);
    }
    const mengen: Record<string, number> = {};
    const extras: Array<{ baseId: string; unit: string; ep: number; qty: number }> = [];
    lvPositionen.forEach((p) => {
      if (p.id.includes("::")) {
        const [baseId, unit] = p.id.split("::");
        if (p.qty > 0 && unit) extras.push({ baseId, unit, ep: p.ep, qty: p.qty });
        return;
      }
      if (p.id.startsWith("custom-")) return;
      if (p.qty > 0) mengen[p.id] = p.qty;
    });
    const activeIds = new Set(lvPositionen.map((p) => p.id));
    STAMMDATEN.forEach((s) =>
      s.positionen.forEach((p) => {
        if (p.aktiv && !activeIds.has(p.id)) mengen[p.id] = 0;
      })
    );
    const manualIdsForPersistence = ids.filter(
      (id) => !id.includes("::") && !id.startsWith("custom-"),
    );
    scheduleLvSave({
      mengen,
      mengenRefs: lvMengenRefs,
      autoFilledPositionIds: lvAutoFilledIds ? Array.from(lvAutoFilledIds) : undefined,
      extras: extras.length > 0 ? extras : undefined,
      epOverrides: Object.keys(nextEpOverrides).length > 0 ? nextEpOverrides : undefined,
      customPositionen:
        lvCustomPositionenRef.current.length > 0 ? lvCustomPositionenRef.current : undefined,
      manuelleEps: manualIdsForPersistence.length > 0 ? manualIdsForPersistence : undefined,
    });
  }, [projectId, lvHydrated, lvPositionen, lvMengenRefs, lvAutoFilledIds, lvInitialEpOverrides, scheduleLvSave]);

  const handleLvMengenRefsChange = useCallback((refs: MengenRefMap) => {
    setLvMengenRefs(refs);
  }, []);

  // Summe aller Aufmaß-Nettoflächen — wird automatisch in alle m²-Positionen
  // im LV-Kalkulator übernommen, solange diese Position noch nicht manuell
  // bearbeitet wurde.
  // Nur m²-Mengen fließen in den LV-Auto-Übertrag ein. Andere Einheiten
  // (Stück, lfm, …) werden ignoriert, damit keine falschen Mengen ins LV
  // gelangen.
  const gesamteArbeitsflaeche = useMemo(() => {
    if (!measurementResults || measurementResults.size === 0) return 0;
    let sum = 0;
    measurementResults.forEach((r) => {
      if (getMeasurementUnit(r) === 'm²') {
        sum += getMeasurementAmount(r);
      }
    });
    return sum;
  }, [measurementResults]);

  // Erstes Erscheinen von Aufmaßdaten → alle m²-Positionen aus STAMMDATEN
  // unter Auto-Fill-Kontrolle stellen, sofern für diese Position noch kein
  // manueller Wert (>0) gesetzt ist (sowohl in der gespeicherten Hydration
  // als auch in der aktuellen Live-LV-Sicht — letzteres deckt manuelle
  // Eingaben ab, die der Nutzer vor dem ersten Aufmaß gemacht hat).
  useEffect(() => {
    if (!lvHydrated || !projectId) return;
    if (lvAutoFilledIds !== null) return;
    if (!measurementResults || measurementResults.size === 0) return;
    const liveQty: Record<string, number> = {};
    lvPositionen.forEach(p => { liveQty[p.id] = p.qty; });
    const m2Ids = STAMMDATEN.flatMap(s => s.positionen)
      .filter(p => p.unit === 'm²')
      .map(p => p.id);
    const seed = new Set<string>(
      m2Ids.filter(id => {
        const stored = lvInitialMengen[id] ?? 0;
        const live = liveQty[id] ?? 0;
        return stored <= 0 && live <= 0;
      })
    );
    setLvAutoFilledIds(seed);
  }, [lvHydrated, projectId, measurementResults, lvAutoFilledIds, lvInitialMengen, lvPositionen]);

  const lvAutoFilledMengen = useMemo<Record<string, number>>(() => {
    if (!lvAutoFilledIds || lvAutoFilledIds.size === 0) return {};
    if (gesamteArbeitsflaeche <= 0) return {};
    const out: Record<string, number> = {};
    lvAutoFilledIds.forEach(id => { out[id] = gesamteArbeitsflaeche; });
    return out;
  }, [lvAutoFilledIds, gesamteArbeitsflaeche]);

  const handleMengeManuallyChanged = useCallback((positionId: string) => {
    setLvAutoFilledIds(prev => {
      if (!prev || !prev.has(positionId)) return prev;
      const next = new Set(prev);
      next.delete(positionId);
      return next;
    });
  }, []);

  const handleHighlightConsumed = useCallback(() => {
    setScrollToLvPosId(null);
  }, []);

  // Map: photoId → stableId (LocalStorage-Photo-ID, identisch zur Server-DB-ID).
  // Wird unten für die Per-Foto-Aggregation gebraucht.
  const stableIdFor = useCallback((transientId: string): string => {
    return photoIdMap.get(transientId) ?? transientId;
  }, [photoIdMap]);

  /**
   * Abgeleitete Sicht aus den Per-Foto-Daten:
   * - lvFromPhotos.mengen: pro LV-Position die Summe aller bestätigten Foto-Mengen.
   * - lvFromPhotos.breakdown: pro LV-Position die Liste aller Foto-Beiträge
   *   (mit berechneter und übernommener Menge), photoIndex aus der Reihenfolge in uploadedPhotos.
   *
   * Wird NICHT automatisch ins LV geschrieben — der Nutzer übernimmt explizit
   * via Button "Aus Fotos ins LV übernehmen".
   */
  /**
   * Per-Foto-Aufstellung für das Angebot: pro Foto die zugeordneten LV-Positionen,
   * die Aufmaßfläche (Netto, falls vorhanden) und die Zwischensumme. Wird sowohl
   * in der Angebotsvorschau als auch im PDF angezeigt.
   */
  const perPhotoQuoteBreakdown = useMemo(() => {
    const entries: Array<{
      photoId: string;
      photoLabel: string;
      fileName?: string;
      amount?: number;
      unit?: string;
      preview?: string;
      positions: Array<{ posId: string; sourceIndex: number; teil?: PhotoLVPosition["teil"]; name: string; beschreibung?: string; qty: number; unit: string; ep: number; total: number; variant?: boolean }>;
      subtotal: number;
    }> = [];
    uploadedPhotos.forEach((photo, photoIndex) => {
      const sid = stableIdFor(photo.id);
      const data = photoSchadensData.get(sid);
      if (!data || data.photoPositionen.length === 0) return;
      // Varianten werden mit ausgegeben, damit sie im Angebot/PDF als
      // Vergleichszeile sichtbar sind. Sie zählen aber NICHT zur Zwischensumme
      // (siehe `subtotal`-Reduce unten) und gehen auch nicht in die LV-Aggregation
      // (`aggregatedPhotoItems` in QuoteGenerator filtert per `pos.variant`).
      const sourceIndexes = data.photoPositionen.map((position, index) => ({ position, index }))
        .filter(({ position }) => position.variant || position.qty > 0).map(({ index }) => index);
      const positions = toQuotePositions(data.photoPositionen).map((position, index) => ({
        ...position, sourceIndex: sourceIndexes[index],
      }));
      if (positions.length === 0) return;
      const subtotal = positions.reduce((s, p) => p.variant ? s : s + p.total, 0);
      const measurement = measurementResults.get(photo.id);
      const measuredAmount = measurement ? getMeasurementAmount(measurement) : undefined;
      const measuredUnit = measurement ? getMeasurementUnit(measurement) : undefined;
      entries.push({
        photoId: photo.id,
        photoLabel: `Foto ${photoIndex + 1}`,
        fileName: getPhotoDisplayName({
          displayName: photo.displayName,
          description: data.description,
          fileName: photo.file?.name,
        }),
        amount: measuredAmount,
        unit: measuredUnit,
        preview: photo.preview,
        positions,
        subtotal,
      });
    });
    return entries;
  }, [uploadedPhotos, photoSchadensData, measurementResults, stableIdFor]);

  const quotePhotos = useMemo(
    () =>
      uploadedPhotos.map((photo) => {
        const sid = stableIdFor(photo.id);
        const schaden = photoSchadensData.get(sid);
        const effectiveTitle = getPhotoDisplayName({
          displayName: photo.displayName,
          description: schaden?.description,
        });
        return effectiveTitle
          ? { ...photo, displayName: effectiveTitle }
          : photo;
      }),
    [uploadedPhotos, photoSchadensData, stableIdFor],
  );

  const facadeReportPhotos = uploadedPhotos.map((photo) => {
    const measurement = measurementResults.get(photo.id);
    return {
      id: photo.id, title: photo.displayName?.trim() || photo.file.name, preview: photo.preview,
      description: photoSchadensData.get(stableIdFor(photo.id))?.description ?? "",
      amount: measurement ? getMeasurementAmount(measurement) : undefined,
      unit: measurement ? getMeasurementUnit(measurement) : undefined,
    };
  });

  /**
   * Aus Per-Foto-Daten ableiten:
   * - Gruppierung nach (Position-ID, Einheit), damit dieselbe Stamm-Position
   *   mit unterschiedlichen Einheiten (z.B. m² vs lfm) als getrennte LV-Zeilen
   *   abgebildet werden kann.
   * - Pro Position-ID: die Einheit mit der größten Gesamtmenge wird "primär"
   *   und füllt mengen[posId] (→ Stamm-Position im LV).
   * - Weitere Einheiten werden als `extras` ausgegeben — die LV-Kalk fügt sie
   *   als zusätzliche Zeilen mit Composite-ID `${posId}::${unit}` ein.
   * - Variant-Positionen (pp.variant === true) werden komplett ignoriert.
   * - EP für extras = mengen-gewichteter Durchschnitt der Foto-EPs in der Gruppe.
   */
  const lvFromPhotos = useMemo<{
    mengen: Record<string, number>;
    breakdown: MengenBreakdownMap;
    extras: Array<{ baseId: string; unit: string; ep: number; qty: number }>;
    epOverrides: Record<string, number>;
  }>(() => {
    type GroupContrib = { photoId: string; photoIndex: number; photoName: string; qty: number; berechneteMenge?: number; ep: number };
    type Group = { posId: string; unit: string; totalQty: number; weightedEpSum: number; contributors: GroupContrib[] };
    const groups = new Map<string, Group>();
    const perPosUnits = new Map<string, Set<string>>();

    uploadedPhotos.forEach((photo, photoIndex) => {
      const sid = stableIdFor(photo.id);
      const data = photoSchadensData.get(sid);
      if (!data || data.photoPositionen.length === 0) return;
      data.photoPositionen.forEach((pp) => {
        if (pp.qty <= 0 || pp.variant) return;
        const key = `${pp.id}::${pp.unit}`;
        let g = groups.get(key);
        if (!g) {
          g = { posId: pp.id, unit: pp.unit, totalQty: 0, weightedEpSum: 0, contributors: [] };
          groups.set(key, g);
        }
        g.totalQty += pp.qty;
        g.weightedEpSum += pp.qty * pp.ep;
        g.contributors.push({
          photoId: sid,
          photoIndex,
          photoName: photo.displayName?.trim() || photo.file.name,
          qty: pp.qty,
          berechneteMenge: pp.berechneteMenge,
          ep: pp.ep,
        });
        if (!perPosUnits.has(pp.id)) perPosUnits.set(pp.id, new Set());
        perPosUnits.get(pp.id)!.add(pp.unit);
      });
    });

    const mengen: Record<string, number> = {};
    const breakdown: MengenBreakdownMap = {};
    const extras: Array<{ baseId: string; unit: string; ep: number; qty: number }> = [];
    const epOverrides: Record<string, number> = {};

    // Für semantische Korrektheit: Stamm-Einheit der Position aus STAMMDATEN
    // ermitteln. Wenn ein Foto-Eintrag mit dieser Einheit existiert, wird
    // dessen Gruppe primär (→ befüllt Stammzeile mit passender Einheit).
    // Andernfalls werden ALLE Gruppen als Extras eingefügt und die Stammzeile
    // bleibt unverändert (statt eine fachlich falsche Einheit anzuzeigen).
    const stammUnitMap = new Map<string, string>();
    STAMMDATEN.forEach((s) => s.positionen.forEach((p) => stammUnitMap.set(p.id, p.unit)));

    perPosUnits.forEach((units, posId) => {
      const stammUnit = stammUnitMap.get(posId);
      const allGroups = Array.from(units).map((u) => groups.get(`${posId}::${u}`)!);
      let primary = stammUnit ? allGroups.find((g) => g.unit === stammUnit) : undefined;
      // Library-Positionen (lib-<id>) sind nicht in STAMMDATEN enthalten —
      // ihre Stammzeile entsteht erst durch den Library-Merge im LV. Damit
      // die Übernahme aus Fotos hier funktioniert, wählen wir die Gruppe mit
      // der größten Gesamtmenge als primäre Einheit. So landet die Menge in
      // `mengen[lib-<id>]` und füllt die Library-Zeile, statt im Extras-Pfad
      // zu verschwinden (der eine bestehende Stammzeile voraussetzt).
      if (!primary && posId.startsWith("lib-") && allGroups.length > 0) {
        primary = allGroups.reduce((a, b) => (b.totalQty > a.totalQty ? b : a));
      }
      const extraGroups = allGroups.filter((g) => g !== primary);

      if (primary) {
        mengen[posId] = primary.totalQty;
        if (primary.totalQty > 0) {
          epOverrides[posId] = primary.weightedEpSum / primary.totalQty;
        }
        breakdown[posId] = primary.contributors.map((c) => ({
          photoId: c.photoId,
          photoIndex: c.photoIndex,
          photoName: c.photoName,
          qty: c.qty,
          berechneteMenge: c.berechneteMenge,
        }));
      }
      extraGroups.forEach((g) => {
        const avgEp = g.totalQty > 0 ? g.weightedEpSum / g.totalQty : 0;
        extras.push({ baseId: posId, unit: g.unit, ep: avgEp, qty: g.totalQty });
        breakdown[`${posId}::${g.unit}`] = g.contributors.map((c) => ({
          photoId: c.photoId,
          photoIndex: c.photoIndex,
          photoName: c.photoName,
          qty: c.qty,
          berechneteMenge: c.berechneteMenge,
        }));
      });
    });

    return { mengen, breakdown, extras, epOverrides };
  }, [uploadedPhotos, photoSchadensData, stableIdFor]);

  const applyLvFromPhotos = useCallback(() => {
    if (!projectId) return;
    const { mengen, extras, epOverrides } = lvFromPhotos;
    const posCount = Object.keys(mengen).length;
    const extrasCount0 = extras.length;
    if (posCount === 0 && extrasCount0 === 0) {
      toast({
        title: "Keine Foto-Mengen vorhanden",
        description: "Erfassen Sie zuerst pro Foto Positionen mit Mengen, bevor Sie ins LV übernehmen.",
      });
      return;
    }
    setLvInitialMengen(mengen);
    setLvInitialMengenRefs({}); // Alte Single-Source-Refs werden vom Breakdown abgelöst.
    setLvInitialExtras(extras);
    // Foto-EPs werden mit bestehenden User-EPs gemergt (Foto gewinnt für gleiche
    // ID), damit manuell editierte EPs für andere Positionen NICHT verloren gehen.
    const mergedEpOverrides = { ...lvInitialEpOverrides, ...epOverrides };
    setLvInitialEpOverrides(mergedEpOverrides);
    // Position-IDs, die jetzt explizit aus Foto-Daten kommen, aus der Auto-Fill-
    // Liste entfernen (manueller Wert hat Vorrang vor Aufmaß-Gesamtfläche).
    // Deterministisch: aus dem aktuellen Snapshot ableiten, dann State + Persistenz
    // mit identischem Wert aktualisieren — kein Verlassen auf async setState-Timing.
    const nextAutoFilledIds: Set<string> | null = lvAutoFilledIds
      ? new Set(Array.from(lvAutoFilledIds).filter(id => !(id in mengen)))
      : null;
    setLvAutoFilledIds(nextAutoFilledIds);
    scheduleLvSave({
      mengen,
      mengenRefs: {},
      autoFilledPositionIds: nextAutoFilledIds ? Array.from(nextAutoFilledIds) : undefined,
      extras: extras.length > 0 ? extras : undefined,
      // Bestehende Custom-Positionen + EP-Overrides aus dem aktuellen lvState
      // bewahren, damit das Full-Replace auf /api/projects/:id/lv-state diese
      // nicht versehentlich verwirft (Issue: Datenverlustrisiko).
      epOverrides:
        Object.keys(mergedEpOverrides).length > 0 ? mergedEpOverrides : undefined,
      customPositionen:
        lvCustomPositionenRef.current.length > 0 ? lvCustomPositionenRef.current : undefined,
      manuelleEps: lvManualEpIds.length > 0 ? lvManualEpIds : undefined,
    });
    // Sofort-Flush: explizite User-Aktion soll nicht im Debounce-Fenster verloren gehen.
    flushLvSave();
    setLvApplyVersion((v) => v + 1); // Erzwingt Remount des LV-Kalkulators.
    const extrasCount = extras.length;
    toast({
      title: "LV aktualisiert",
      description: extrasCount > 0
        ? `${posCount} Position(en) aus Fotodaten übernommen + ${extrasCount} zusätzliche Einheits-Variante(n).`
        : `${posCount} Position(en) aus Fotodaten übernommen.`,
    });
  }, [projectId, lvFromPhotos, toast]);

  useEffect(() => {
    if (scrollToPhotoIndex === null || currentStep !== 3) return;
    const idx = scrollToPhotoIndex;
    const el = document.querySelector(`[data-photo-index="${idx}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setHighlightTarget({ index: idx, key: Date.now() });
    setScrollToPhotoIndex(null);
  }, [scrollToPhotoIndex, currentStep]);

  // Deep-Link aus der Foto-Analyse-Seite: ?focusLvPos=<positionId>
  // → springt nach Hydration zu Schritt 4 und scrollt die frisch übernommene
  //    Custom-Position im LV-Kalkulator ins Bild + hebt sie kurz hervor.
  // Wird genau einmal pro Pageload konsumiert; der URL-Param wird danach
  // entfernt, damit ein erneutes Mounten nicht erneut springt.
  const focusLvConsumedRef = useRef(false);
  useEffect(() => {
    if (focusLvConsumedRef.current) return;
    if (!lvHydrated || !projectId) return;
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const focusId = params.get("focusLvPos");
    if (!focusId) return;
    focusLvConsumedRef.current = true;
    handleScrollToLvPosition(focusId);
    // URL säubern, damit Reloads nicht endlos triggern.
    params.delete("focusLvPos");
    const cleanUrl =
      window.location.pathname +
      (params.toString() ? `?${params.toString()}` : "") +
      window.location.hash;
    window.history.replaceState({}, "", cleanUrl);
  }, [lvHydrated, projectId, handleScrollToLvPosition]);

  // Deep-Link aus der Foto-Analyse-Seite: ?focusPhotoId=<numericPhotoId>
  // → springt nach Hydration zu Schritt 3 und scrollt zum genau gemeinten
  //    Schadensbild (per AreaMeasurement-Highlight). Wird genau einmal pro
  //    Pageload konsumiert; URL-Param wird danach entfernt.
  const focusPhotoConsumedRef = useRef(false);
  useEffect(() => {
    if (focusPhotoConsumedRef.current) return;
    if (!projectId) return;
    if (typeof window === "undefined") return;
    if (uploadedPhotos.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const focusPhotoId = params.get("focusPhotoId");
    if (!focusPhotoId) return;
    // Index in der aktuellen Foto-Reihenfolge bestimmen, indem wir vom
    // transienten Foto-ID via photoIdMap auf die stabile (Server-)ID mappen.
    const idx = uploadedPhotos.findIndex((p) => getStablePhotoId(p.id) === focusPhotoId);
    if (idx < 0) return; // Foto noch nicht hydratisiert – Effekt wartet auf nächste Änderung.
    focusPhotoConsumedRef.current = true;
    handleGoToMeasurementPhoto(idx);
    params.delete("focusPhotoId");
    const cleanUrl =
      window.location.pathname +
      (params.toString() ? `?${params.toString()}` : "") +
      window.location.hash;
    window.history.replaceState({}, "", cleanUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, uploadedPhotos, photoIdMap, handleGoToMeasurementPhoto]);

  const nextStep = () => {
    if (currentStep < totalSteps) goToStep(currentStep + 1);
  };

  const previousStep = () => {
    if (currentStep > 1) goToStep(currentStep - 1);
  };

  // Manueller "Speichern"-Button: erzwingt sofortiges Schreiben aller Pending-
  // Patches und schreibt zusätzlich die Projekt-Grunddaten. Wenn AUTO_SAVE_ENABLED
  // wieder deaktiviert würde, wäre das der einzige Pfad zum Server. Reihenfolge:
  //   1) Foto-Metadaten flushen (Beschreibung, Positionen, Aufmaß)
  //   2) lvState + quoteData flushen (debounced Refs leeren und PATCH senden)
  //   3) Projekt-Grunddaten via Form-Submit (PUT /api/projects/:id) speichern
  const saveProgress = () => {
    flushAllPhotoSaves();
    flushLvSave();
    flushQuoteSave();
    form.handleSubmit(onSubmit)();
  };

  const completeProject = async () => {
    try {
      await updateProjectMutation.mutateAsync({
        status: "completed",
        currentStep: totalSteps,
      });
      toast({
        title: "Projekt abgeschlossen",
        description:
          'Das Projekt findest du jetzt unter „Abgeschlossene Angebote".',
      });
      navigate("/completed");
    } catch {
      // Fehler-Toast feuert bereits in updateProjectMutation.onError —
      // hier kein zusätzlicher Toast, kein Redirect.
    }
  };

  const progressPercentage = (currentStep / totalSteps) * 100;

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-bauwerk-orange rounded-full flex items-center justify-center mx-auto mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
          <p className="text-gray-600">Projekt wird geladen...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Projekt nicht gefunden</h1>
          <Button onClick={() => navigate("/dashboard")}>
            Zurück zum Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const getStepTitle = (step: number) => {
    switch (step) {
      case 1: return "Grunddaten";
      case 2: return "Technische Daten";
      case 3: return "Dokumentation";
      case 4: return "Angebotserstellung";
      default: return "Unbekannt";
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div>
          <h1 className="text-4xl font-bold text-bauwerk-teal mb-2">
            {project.name}
          </h1>
          <p className="text-gray-600">Projekt bearbeiten - {getStepTitle(currentStep)}</p>
          <div className="flex items-center gap-2 mt-2">
            <Badge className="bg-bauwerk-mint text-bauwerk-teal">
              {formatActivityLabel(project.type)}
            </Badge>
            <Badge variant="outline">
              Schritt {currentStep} von {totalSteps}
            </Badge>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/project/${projectId}/invoices`)}
            className="border-bauwerk-teal text-bauwerk-teal hover:bg-bauwerk-mint"
          >
            <FileText className="w-4 h-4 mr-2" />
            Rechnungen
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/project/${projectId}/protokoll`)}
            className="border-bauwerk-teal text-bauwerk-teal hover:bg-bauwerk-mint"
          >
            <FileText className="w-4 h-4 mr-2" />
            Protokoll
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/dashboard")}
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück zum Dashboard
          </Button>
        </div>
      </div>
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium text-bauwerk-teal">
            Fortschritt: {currentStep} von {totalSteps}
          </span>
          <span className="text-sm text-gray-600">{getStepTitle(currentStep)}</span>
        </div>
        <Progress value={progressPercentage} className="h-3 mb-4" />
        <div className="grid grid-cols-4 gap-2">
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => {
            const reachable = step <= maxStepReached;
            const isActive = step === currentStep;
            return (
              <button
                key={step}
                type="button"
                onClick={() => reachable && goToStep(step)}
                disabled={!reachable}
                data-testid={`step-button-${step}`}
                className={`flex flex-col items-center gap-1 rounded-lg border p-2 text-xs transition-colors ${
                  isActive
                    ? "border-bauwerk-orange bg-bauwerk-orange/10 text-bauwerk-orange font-semibold"
                    : reachable
                    ? "border-gray-300 bg-white text-gray-700 hover:bg-gray-50 cursor-pointer"
                    : "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                }`}
              >
                <span className={`flex items-center justify-center w-6 h-6 rounded-full text-[11px] ${
                  isActive
                    ? "bg-bauwerk-orange text-white"
                    : reachable
                    ? "bg-bauwerk-teal text-white"
                    : "bg-gray-300 text-white"
                }`}>{step}</span>
                <span className="text-center leading-tight">{getStepTitle(step)}</span>
              </button>
            );
          })}
        </div>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-bauwerk-teal">
            Schritt {currentStep}: {getStepTitle(currentStep)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className={currentStep === 1 ? "" : "hidden"}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Projektname *</Label>
                  <Input
                    id="name"
                    placeholder="z.B. Neubau Bürogebäude"
                    {...form.register("name", { required: "Projektname ist erforderlich" })}
                    className="focus:ring-bauwerk-teal focus:border-bauwerk-teal"
                  />
                  {form.formState.errors.name && (
                    <p className="text-sm text-red-600">{form.formState.errors.name.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Standort *</Label>
                  <Input
                    id="address"
                    placeholder="Straße, PLZ Ort"
                    {...form.register("address", { required: "Standort ist erforderlich" })}
                    className="focus:ring-bauwerk-teal focus:border-bauwerk-teal"
                  />
                  {form.formState.errors.address && (
                    <p className="text-sm text-red-600">{form.formState.errors.address.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customer">Auftraggeber</Label>
                  <Input
                    id="customer"
                    placeholder="Firmenname oder Privatperson"
                    {...form.register("customer")}
                    className="focus:ring-bauwerk-teal focus:border-bauwerk-teal"
                  />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="description">Projektbeschreibung</Label>
                  <Textarea
                    id="description"
                    rows={4}
                    placeholder="Kurze Beschreibung des Bauprojekts..."
                    {...form.register("description")}
                    className="focus:ring-bauwerk-teal focus:border-bauwerk-teal"
                  />
                </div>
              </div>
            </div>

            <div className={currentStep === 2 ? "" : "hidden"}>
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-bauwerk-teal">Technische Spezifikationen</h3>
                <div className="space-y-2">
                  <Label>Objektbeschaffenheit <span className="text-xs text-gray-500 font-normal">(Mehrfachauswahl möglich)</span></Label>
                  <MultiSelectToggle
                    options={objektbeschaffenheitOptions}
                    selected={form.watch("objektbeschaffenheit") ?? []}
                    onChange={(vals) => form.setValue("objektbeschaffenheit", vals)}
                    editable
                  />
                </div>
                <div className="space-y-2">
                  <Label>Maßnahmen <span className="text-xs text-gray-500 font-normal">(Mehrfachauswahl möglich)</span></Label>
                  <MultiSelectToggle
                    options={massnahmenOptions}
                    selected={form.watch("massnahmen") ?? []}
                    onChange={(vals) => form.setValue("massnahmen", vals)}
                    editable
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Geschosszahl</Label>
                    <Input
                      type="number"
                      min={0}
                      placeholder="z.B. 3"
                      {...form.register("geschosszahl", { valueAsNumber: true })}
                      className="focus:ring-bauwerk-teal focus:border-bauwerk-teal"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Höhe in m</Label>
                    <Input
                      type="number"
                      min={0}
                      step="0.1"
                      placeholder="z.B. 9.5"
                      {...form.register("hoehe", { valueAsNumber: true })}
                      className="focus:ring-bauwerk-teal focus:border-bauwerk-teal"
                    />
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <Label>Menge / Ausmaß</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        placeholder="z.B. 150"
                        {...form.register("menge", { valueAsNumber: true })}
                        className="focus:ring-bauwerk-teal focus:border-bauwerk-teal flex-1"
                      />
                      <select
                        {...form.register("mengeneinheit")}
                        className="w-32 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bauwerk-teal focus:border-bauwerk-teal"
                      >
                        {mengeneinheitOptions.map((unit) => (
                          <option key={unit} value={unit}>{unit}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={currentStep === 3 ? "" : "hidden"}>
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-bauwerk-teal">Dokumentation</h3>
                <FacadeReportExport projectName={form.watch("name") || project.name}
                  customerName={form.watch("customer") || project.customer || ""}
                  address={form.watch("address") || project.address || ""}
                  description={form.watch("description") ?? project.description ?? ""}
                  photos={facadeReportPhotos} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-base font-semibold text-bauwerk-teal mb-4">Fotos hochladen</h4>
                    <FileUpload
                      accept="image/*"
                      multiple={true}
                      maxSize={5}
                      title="Baustellenfotos"
                      description="Drag & Drop oder klicken zum Auswählen von Fotos"
                      icon={<Camera className="w-12 h-12 text-gray-400 mx-auto" />}
                      allowCamera={true}
                      initialFiles={photoInitialFiles}
                      onFilesChange={handlePhotosUploaded}
                      onDeleteRequest={handlePhotoDelete}
                    />
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-bauwerk-teal mb-4">Pläne & Dokumente</h4>
                    <FileUpload
                      accept=".pdf,.dwg,.dxf,.doc,.docx,.xls,.xlsx"
                      multiple={true}
                      maxSize={10}
                      title="Dokumente"
                      description="PDF, DWG, Office-Dokumente"
                      icon={<Upload className="w-12 h-12 text-gray-400 mx-auto" />}
                      allowCamera={false}
                      onFilesChange={setUploadedDocuments}
                    />
                  </div>
                </div>


                {/* Flächenmessung für hochgeladene Fotos */}
                {uploadedPhotos.length > 0 && (
                  <div className="space-y-4">
                    {uploadedPhotos.map((photo, photoIdx) => {
                      const stableId = getStablePhotoId(photo.id);
                      const schaden = photoSchadensData.get(stableId) ?? { description: "", photoPositionen: [] };
                      const measuredEntry = measurementResults.get(photo.id);
                      const measured = measuredEntry ? getMeasurementAmount(measuredEntry) : undefined;
                      return (
                        <div
                          key={photo.id}
                          data-photo-index={photoIdx}
                          className="relative"
                        >
                          <div className="mb-2 flex items-center justify-end gap-1">
                            <span className="mr-1 flex items-center gap-1 text-xs text-gray-500">
                              <GripVertical className="h-4 w-4" />
                              Reihenfolge
                            </span>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              disabled={photoIdx === 0}
                              onClick={() => movePhotoBlock(photoIdx, photoIdx - 1)}
                              title="Foto nach oben verschieben"
                              aria-label="Foto nach oben verschieben"
                            >
                              <ArrowUp className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              disabled={photoIdx === uploadedPhotos.length - 1}
                              onClick={() => movePhotoBlock(photoIdx, photoIdx + 1)}
                              title="Foto nach unten verschieben"
                              aria-label="Foto nach unten verschieben"
                            >
                              <ArrowDown className="h-4 w-4" />
                            </Button>
                          </div>
                          <AreaMeasurement
                            photo={photo}
                            highlightKey={highlightTarget?.index === photoIdx ? highlightTarget.key : undefined}
                            serverPhotoId={photo.serverPhotoId}
                            onAnnotationSaved={(newPreviewUrl) => {
                              setUploadedPhotos((prev) =>
                                prev.map((p) =>
                                  p.id === photo.id
                                    ? { ...p, preview: newPreviewUrl }
                                    : p,
                                ),
                              );
                            }}
                            initialAmount={measuredEntry ? getMeasurementAmount(measuredEntry) : undefined}
                            initialAmountFormula={measuredEntry?.amountFormula ?? measuredEntry?.netAreaFormula}
                            initialUnit={measuredEntry ? getMeasurementUnit(measuredEntry) : undefined}
                            initialNetArea={measuredEntry?.netArea}
                            initialNetAreaFormula={measuredEntry?.netAreaFormula}
                            onMeasurementComplete={(result) => {
                              // Schreibt in den lokalen measurementResults-State.
                              // KEINE automatische Weitergabe an LV — das passiert ausschließlich
                              // bewusst über den Button "Aus Fotos ins LV übernehmen" in Schritt 4.
                              setMeasurementResults((prev) => {
                                const next = new Map(prev);
                                next.set(photo.id, result);
                                return next;
                              });
                              // Persistenz: lokal (über globalen Debouncer, max 1
                              // Flush/500 ms gesammelt über alle Foto-Instanzen) +
                              // server (wenn Foto bereits hochgeladen).
                              if (projectId) {
                                try {
                                  enqueueMeasurementSave(projectId, {
                                    photoId: photo.id,
                                    amount: result.amount,
                                    amountFormula: result.amountFormula,
                                    unit: result.unit,
                                    mengen: result.mengen,
                                    scale: result.scale,
                                  });
                                } catch (err) {
                                  console.error('Messdaten konnten nicht lokal gespeichert werden:', err);
                                }
                              }
                              const stable = photoIdMapRef.current.get(photo.id) ?? photo.id;
                              const numericId = Number(stable);
                              const measurementPatch: PhotoPendingPatch = {
                                measurementData: {
                                  schemaVersion: 1,
                                  updatedAt: new Date().toISOString(),
                                  amount: result.amount,
                                  amountFormula: result.amountFormula,
                                  unit: result.unit,
                                  mengen: result.mengen,
                                  scale: result.scale,
                                },
                              };
                              if (Number.isInteger(numericId) && numericId > 0) {
                                schedulePhotoSave(stable, measurementPatch);
                              } else {
                                enqueuePhotoOp(photo.id, (serverId) => {
                                  PhotoApi.updateMetadata(serverId, measurementPatch).catch((err) =>
                                    console.error('Messdaten (post-upload) nicht gespeichert:', err),
                                  );
                                });
                              }
                            }}
                          />
                          {(() => {
                            const numericPhotoId = Number(stableId);
                            const isPersisted =
                              Number.isInteger(numericPhotoId) && numericPhotoId > 0;
                            const canAnalyze = isPersisted && !!projectId;
                            return (
                              <div className="mt-2 flex justify-end">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  disabled={!canAnalyze}
                                  onClick={() =>
                                    navigate(
                                      `/photo-analysis?projectId=${projectId}&photoId=${numericPhotoId}`,
                                    )
                                  }
                                  title={
                                    canAnalyze
                                      ? "Foto-Analyse für genau dieses Foto öffnen"
                                      : "Foto wird noch gespeichert — bitte kurz warten."
                                  }
                                  data-testid={`button-photo-analyze-${stableId}`}
                                >
                                  <Sparkles className="w-4 h-4 mr-2" />
                                  Mit Foto-Analyse analysieren
                                </Button>
                              </div>
                            );
                          })()}
                          <PhotoSchadenserfassung
                            photoId={stableId}
                            photoName={photo.file.name}
                            photoTitle={photo.displayName ?? ""}
                            description={schaden.description}
                            photoPositionen={schaden.photoPositionen}
                            measuredArea={measured}
                            otherPhotosWithPositions={uploadedPhotos
                              .filter((other) => other.id !== photo.id)
                              .map((other) => {
                                const otherStableId = getStablePhotoId(other.id);
                                const otherSchaden =
                                  photoSchadensData.get(otherStableId);
                                return {
                                  photoId: otherStableId,
                                  photoName: other.displayName?.trim() || other.file.name,
                                  positionen: otherSchaden?.photoPositionen ?? [],
                                };
                              })
                              .filter((s) => s.positionen.length > 0)}
                            onDescriptionChange={(desc) => updatePhotoSchaden(photo.id, "description", desc)}
                            onPhotoTitleChange={(title) => {
                              setUploadedPhotos((prev) =>
                                prev.map((p) => p.id === photo.id ? { ...p, displayName: title } : p),
                              );
                              const normalized = title.trim() || null;
                              const numericPhotoId = Number(stableId);
                              if (Number.isInteger(numericPhotoId) && numericPhotoId > 0) {
                                schedulePhotoSave(stableId, { displayName: normalized });
                              } else {
                                enqueuePhotoOp(photo.id, (serverId) => {
                                  PhotoApi.updateMetadata(serverId, { displayName: normalized }).catch((err) =>
                                    console.error("Fototitel konnte nicht gespeichert werden:", err),
                                  );
                                });
                              }
                            }}
                            onPositionenChange={(positionen) => updatePhotoSchaden(photo.id, "photoPositionen", positionen)}
                            onJumpToLv={handleScrollToLvPosition}
                          />
                        </div>
                      );
                    })}
                    
                    {/* Ergebnisse zusammenfassen */}
                    {measurementResults.size > 0 && (
                      <div className="mt-6 p-4 bg-bauwerk-mint border border-bauwerk-teal rounded-lg">
                        <h5 className="font-semibold text-bauwerk-teal mb-3">Erfasste Mengen:</h5>
                        <div className="space-y-2">
                          {Array.from(measurementResults.entries()).map(([photoId, result]) => {
                            const photo = uploadedPhotos.find(p => p.id === photoId);
                            const amt = getMeasurementAmount(result);
                            const u = getMeasurementUnit(result);
                            return (
                              <div key={photoId} className="flex justify-between items-center">
                                <span className="text-sm">
                                  {photo?.file.name || `Foto ${photoId}`}
                                </span>
                                <span className="font-medium text-bauwerk-teal">
                                  {amt.toFixed(2).replace('.', ',')} {u}
                                </span>
                              </div>
                            );
                          })}
                          {gesamteArbeitsflaeche > 0 && (
                            <div className="border-t pt-2 mt-2">
                              <div className="flex justify-between items-center font-bold">
                                <span>Gesamtfläche (m²):</span>
                                <span className="text-bauwerk-teal">
                                  {formatArea(gesamteArbeitsflaeche)}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Zusätzliche Notizen</Label>
                  <Textarea 
                    rows={4}
                    placeholder="Besonderheiten, Herausforderungen, Baumängel, etc."
                    className="focus:ring-bauwerk-teal focus:border-bauwerk-teal"
                  />
                </div>
              </div>
            </div>

            {/* Schritt 4 wird wie die Schritte 1–3 nur per CSS aus-/eingeblendet,
                damit der Angebots-Block (inkl. seines internen React-State und
                der manuell hinzugefügten Pauschalpositionen) beim Wechsel zu
                anderen Schritten nicht aus dem Baum entfernt und wieder neu
                aus initialQuoteData re-initialisiert wird. Siehe Task 97. */}
            <div className={currentStep === 4 ? "" : "hidden"}>
              <div className="space-y-6">
                {/* Rücksprung-Hinweis: erscheint, sobald der Nutzer aus dem
                    Plausi-Block ins LV gesprungen ist. Klick führt zurück
                    zur Plausibilitätsprüfung mit gleichem Aufklapp-Zustand. */}
                {plausibilityReturn && (
                  <div
                    className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-4 py-2.5 flex items-center justify-between gap-3"
                    data-testid="plausibility-return-banner"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <ArrowLeft className="w-4 h-4 text-amber-700 dark:text-amber-300 shrink-0" />
                      <span className="text-sm text-amber-900 dark:text-amber-100 truncate">
                        Sie kommen aus der Plausibilitätsprüfung.
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleReturnToPlausibility}
                      className="h-7 text-xs shrink-0 border-amber-400 text-amber-900 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-900/40"
                      data-testid="button-return-to-plausibility"
                    >
                      Zurück zur Plausibilitätsprüfung
                    </Button>
                  </div>
                )}
                {/* Per-photo damage summary — read-only reference */}
                {uploadedPhotos.some((p) => {
                   const d = photoSchadensData.get(getStablePhotoId(p.id));
                  return d && (d.description.trim() || d.photoPositionen.length > 0);
                }) && (
                  <div className="border border-amber-200 rounded-xl bg-amber-50/60">
                    <button
                      type="button"
                      onClick={() => setPhotoSchadensSummaryOpen((v) => !v)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-amber-100/60 transition-colors rounded-xl"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-base">📸</span>
                        <span className="text-sm font-semibold text-amber-800">
                          Schadenserfassung je Foto (Referenz)
                        </span>
                        <Badge className="bg-amber-200 text-amber-900 text-xs">
                          {uploadedPhotos.filter((p) => {
                             const d = photoSchadensData.get(getStablePhotoId(p.id));
                            return d && d.photoPositionen.length > 0;
                          }).length} Fotos mit Positionen
                        </Badge>
                      </div>
                      {photoSchadensSummaryOpen ? (
                        <ChevronUp className="w-4 h-4 text-amber-600 shrink-0" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-amber-600 shrink-0" />
                      )}
                    </button>
                    {photoSchadensSummaryOpen && (
                      <div className="border-t border-amber-200 px-4 py-3 space-y-4">
                        <p className="text-xs text-amber-700">
                          Diese Übersicht zeigt die im Schritt 3 erfassten Schäden je Foto als Referenz. Die Mengen werden nicht automatisch in den Kalkulator übernommen.
                        </p>
                        {uploadedPhotos.map((photo, idx) => {
                           const schaden = photoSchadensData.get(getStablePhotoId(photo.id));
                          if (!schaden || (!schaden.description.trim() && schaden.photoPositionen.length === 0)) return null;
                          const subtotal = schaden.photoPositionen.reduce((sum, p) => sum + p.qty * p.ep, 0);
                          return (
                            <div key={photo.id} className="rounded-lg border border-amber-200 bg-white p-3 space-y-2">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <span className="text-xs font-semibold text-amber-800">
                                    Foto {idx + 1}: {photo.displayName?.trim() || photo.file.name}
                                  </span>
                                  {schaden.description.trim() && (
                                    <p className="text-xs text-gray-600 mt-0.5">{schaden.description}</p>
                                  )}
                                </div>
                                {subtotal > 0 && (
                                  <span className="text-sm font-semibold tabular-nums text-emerald-700 shrink-0">
                                    € {formatAmount(subtotal)}
                                  </span>
                                )}
                              </div>
                              {schaden.photoPositionen.length > 0 && (
                                <div className="space-y-1">
                                  {schaden.photoPositionen.map((pos) => (
                                    <div key={pos.id} className="flex items-center justify-between text-xs text-gray-600">
                                      <span className="truncate">{pos.name}</span>
                                      <span className="shrink-0 ml-2 tabular-nums">
                                        {pos.qty} {pos.unit} · € {formatAmount(pos.qty * pos.ep)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {uploadedPhotos.filter((p) => {
                           const d = photoSchadensData.get(getStablePhotoId(p.id));
                          return d && d.photoPositionen.length > 0;
                        }).length > 0 && (
                          <div className="flex justify-between items-center border-t border-amber-200 pt-2">
                            <span className="text-xs font-semibold text-amber-800">Summe Schadenserfassung (netto)</span>
                            <span className="text-sm font-bold tabular-nums text-emerald-700">
                              € {formatAmount(
                                Array.from(photoSchadensData.values()).reduce(
                                  (total, d) => total + d.photoPositionen.reduce((s, p) => s + p.qty * p.ep, 0),
                                  0
                                )
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Direkt-Einstieg in die Foto-Analyse mit vorausgewähltem Projekt. */}
                <div className="mb-3 p-4 rounded-xl border border-bauwerk-teal/30 bg-bauwerk-teal/5 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h4 className="text-sm font-semibold text-bauwerk-teal mb-1 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      Foto-Analyse für dieses Projekt
                    </h4>
                    <p className="text-xs text-gray-700">
                      Lass die KI aus Fotos einen Schadensbericht oder eine Leistungsbeschreibung
                      entwerfen. Übernommene Texte landen direkt im LV dieses Projekts — ohne
                      weiteren Projekt-Picker.
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={() => navigate(`/photo-analysis?projectId=${projectId}`)}
                    className="shrink-0 bg-bauwerk-teal hover:bg-bauwerk-teal/90 text-white"
                    data-testid="open-photo-analysis-for-project"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Foto-Analyse starten
                  </Button>
                </div>

                {/* Bewusste Übernahme aus Per-Foto-Daten ins LV.
                    Wahrheitsquelle bleibt photoSchadensData (pro Foto, pro Position, mit qty);
                    LV ist abgeleitet, aber danach manuell editierbar. */}
                {(() => {
                  const photoPosCount = Object.keys(lvFromPhotos.mengen).length;
                  const photoTotalQty = Object.values(lvFromPhotos.mengen).reduce((s, v) => s + v, 0);
                  return (
                    <div className="mb-3 p-4 rounded-xl border border-bauwerk-mint bg-bauwerk-mint/30 flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <h4 className="text-sm font-semibold text-bauwerk-teal mb-1">
                          Aus Fotodaten ins LV übernehmen
                        </h4>
                        <p className="text-xs text-gray-700">
                          {photoPosCount > 0
                            ? `${photoPosCount} Position(en) aus den Fotos verfügbar (Σ Mengen: ${photoTotalQty.toFixed(2)}). Übernahme überschreibt manuell gesetzte LV-Mengen für betroffene Positionen.`
                            : "Noch keine bestätigten Mengen in den Fotos. Erfassen Sie pro Foto Positionen mit Menge in Schritt 3."}
                        </p>
                      </div>
                      <Button
                        type="button"
                        onClick={applyLvFromPhotos}
                        disabled={photoPosCount === 0}
                        className="shrink-0 bg-bauwerk-orange hover:bg-bauwerk-light-orange text-white disabled:opacity-40"
                        data-testid="apply-lv-from-photos"
                      >
                        Aus Fotos übernehmen
                      </Button>
                    </div>
                  );
                })()}

                {/* Hinweis seit Task #109: Im Kalkulator werden hier nur noch
                    die Pauschalleistungen zur Bearbeitung gezeigt. Alle übrigen
                    Positionen (Beton, HDW, Bewehrung, Mörtel, Risse,
                    Beschichtung, Leistungspositionen) werden über die Foto-
                    Erfassung in Schritt 3 + die obige Übernahme-Schaltfläche
                    gepflegt und fließen unverändert ins Angebots-PDF und in
                    die Gesamtsumme ein. */}
                <p className="text-xs text-gray-600 mb-2">
                  Hier können Sie die <strong>Pauschalleistungen</strong>{" "}
                  und bewusst übernommene <strong>Kalkulationsvorlagen</strong>{" "}
                  bearbeiten. Mit <strong>„Maßaufstellung importieren“</strong>{" "}
                  übernehmen Sie Mengen aus Excel/CSV, auf Wunsch zusammen mit
                  den Preisen einer Kalkulationsvorlage. Alle anderen
                  Positionen entstehen automatisch aus der Schadenserfassung in
                  Schritt 3 und erscheinen unverändert im Angebots-PDF und in
                  der Gesamtsumme.
                </p>

                <LeistungsverzeichnisKalkulator
                  initialTeile={lvTeile}
                  onTeileChange={handleLvTeileChange}
                  key={`lv-${lvApplyVersion}`}
                  onPositionenChange={handleLvPositionenChange}
                  onMengenRefsChange={handleLvMengenRefsChange}
                  initialMengen={lvInitialMengen}
                  initialMengenRefs={lvInitialMengenRefs}
                  injectedExtras={lvInitialExtras}
                  initialEpOverrides={lvInitialEpOverrides}
                  initialCustomPositionen={lvInitialCustomPositionen}
                  onCustomPositionenChange={handleLvCustomPositionenChange}
                  initialCalculations={lvInitialCalculations}
                  onCalculationsChange={handleLvCalculationsChange}
                  initialSectionTitles={lvSectionTitles}
                  onSectionTitlesChange={handleLvSectionTitlesChange}
                  initialManualEpIds={lvManualEpIds}
                  onManualEpIdsChange={handleLvManualEpIdsChange}
                  currentProjectId={projectId ? Number(projectId) : undefined}
                  onBeforeMeasurementImport={async () => {
                    await flushLvSave();
                    // An older delayed PATCH must not overwrite the atomic
                    // import after its source/report/quantities are committed.
                    await lvSaveInFlightRef.current;
                  }}
                  autoFilledMengen={lvAutoFilledMengen}
                  onMengeManuallyChanged={handleMengeManuallyChanged}
                  availableMengen={availableMengen}
                  mengenBreakdown={lvFromPhotos.breakdown}
                  highlightPositionId={scrollToLvPosId}
                  onHighlightConsumed={handleHighlightConsumed}
                  onGoToMeasurementPhoto={handleGoToMeasurementPhoto}
                  plausibilityByPosId={plausibilityByLvPosId}
                  onJumpToPlausibility={handleJumpToPlausibilityFromLv}
                  isExistingOffer={lvLoadState === 'loaded_with_data'}
                  // Schritt 4 zeigt im Kalkulator nur noch die Pauschalleistungen
                  // (s7). Die übrigen Sektionen (Beton, HDW, Bewehrung, Mörtel,
                  // Risse, Beschichtung, Leistungspositionen) werden weiterhin
                  // im State geführt, aus den Foto-Daten gefüllt und unverändert
                  // ins Angebots-PDF und die Gesamtsumme aufgenommen — sie
                  // werden hier lediglich nicht zur manuellen Bearbeitung
                  // angezeigt. Siehe Task #109.
                  visibleSektionIds={["s7"]}
                />
                {/* QuoteGenerator wird erst gerendert, NACHDEM die Server-First-
                    Hydration für quoteData abgeschlossen ist. Damit greifen die
                    useState-Initializer in QuoteGenerator den korrekten
                    Server-/Migrations-Stand und es entsteht kein Render mit
                    leeren Defaults, der den Server überschreiben könnte. */}
                <FacadeReportExport projectName={form.watch("name") || project.name}
                  customerName={form.watch("customer") || project.customer || ""}
                  address={form.watch("address") || project.address || ""}
                  description={form.watch("description") ?? project.description ?? ""}
                  photos={facadeReportPhotos} />
                {quoteLoadState !== 'not_loaded' && (
                  <QuoteGenerator
                    onImportMissingServices={lvHydrated ? handleImportMissingServices : undefined}
                    onLvTeilChange={(id, teil) => handleLvTeileChange({ ...lvTeileRef.current, [id]: teil })}
                    onPhotoTeilChange={(photoId, sourceIndex, teil) => {
                      const data = photoSchadensData.get(stableIdFor(photoId));
                      if (!data) return;
                      updatePhotoSchaden(photoId, "photoPositionen", data.photoPositionen.map((position, index) =>
                        sourceIndex === null || index === sourceIndex ? { ...position, teil } : position));
                    }}
                    projectId={projectId}
                    projectName={form.watch("name") || project.name}
                    customerName={form.watch("customer") || project.customer || ''}
                    projectAddress={form.watch("address") || project.address || ''}
                    projectDescription={form.watch("description") ?? project.description ?? undefined}
                    projectDetails={{
                      activity: (form.watch("type") || project.type) ?? undefined,
                      objektbeschaffenheit: form.watch("objektbeschaffenheit") ?? project.objektbeschaffenheit ?? undefined,
                      massnahmen: form.watch("massnahmen") ?? project.massnahmen ?? undefined,
                      geschosszahl: form.watch("geschosszahl") ?? project.geschosszahl ?? null,
                      hoehe: form.watch("hoehe") ?? project.hoehe ?? null,
                      menge: form.watch("menge") ?? project.menge ?? null,
                      mengeneinheit: form.watch("mengeneinheit") ?? project.mengeneinheit ?? undefined,
                    }}
                    photos={quotePhotos}
                    documents={uploadedDocuments}
                    measurementResults={measurementResults}
                    lvPositionen={lvPositionen}
                    lvSummen={lvSummen}
                    lvMengenRefs={lvMengenRefs}
                    onScrollToLvPosition={handleScrollToLvPosition}
                    onJumpFromPlausibility={handleJumpFromPlausibility}
                    onUpdateLvPositionEp={handlePlausibilityEpUpdate}
                    plausibilityRestoreHint={plausibilityRestoreHint}
                    onPlausibilityRestoreConsumed={handlePlausibilityRestoreConsumed}
                    onScrollToProjectDescription={handleScrollToProjectDescription}
                    onPlausibilityFindingsByPosIdChange={handlePlausibilityFindingsByPosIdChange}
                    availableMengen={availableMengen}
                    perPhotoBreakdown={perPhotoQuoteBreakdown}
                    projectCalculations={lvInitialCalculations}
                    projectSectionTitles={lvSectionTitles}
                    initialQuoteData={initialQuoteData}
                    onQuoteChange={scheduleQuoteSave}
                  />
                )}
              </div>
            </div>

            <div className="flex justify-between pt-6 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={previousStep}
                disabled={currentStep === 1}
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück
              </Button>
              <div className="space-x-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={saveProgress}
                  disabled={updateProjectMutation.isPending}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Speichern
                </Button>
                {currentStep < totalSteps ? (
                  <Button
                    type="button"
                    onClick={nextStep}
                    className="bg-bauwerk-orange hover:bg-bauwerk-light-orange"
                  >
                    Weiter
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={completeProject}
                    disabled={updateProjectMutation.isPending}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {updateProjectMutation.isPending ? "Abschließen..." : "Projekt abschließen"}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

    </div>
  );
}
